import React, { useState, useRef, useEffect } from "react";
import { Plus, Eye, CheckCircle, Clock, AlertTriangle, Users, Monitor, UserCheck, RefreshCw, Search, X, Upload, FileText, Edit3, Activity, ArrowRightLeft, History, ChevronRight, Download, ShieldCheck, PackageCheck, Building2 } from "lucide-react";
import api, { BASE } from "../api";
import { uploadUrl } from "../uploadUrl";
import { K } from "../theme";
import { Bg, Bt, Cd, Sl, Inp, Tbl, Stat, Av, Pagination, usePagination } from "../components";
import { notify, confirmDialog } from "../notify";
import { fmtDMY, fmtDDMMYY } from "../dateFormat";

// Per-category Add Asset form spec.
//   · `top: true`     → field maps to a top-level column on `assets`
//                       (brand/model/serial_number/purchase_date/purchase_cost/warranty_expiry).
//   · `type: 'file'`  → file is uploaded first via /api/assets/upload-file
//                       and the {path,name} is stored under attributes[key].
//   · `type: 'subtype'` → renders a dropdown of asset_categories filtered by
//                         the selected group; the choice becomes category_code.
//   · everything else → goes into the `attributes` JSONB column.
// `wide: true` makes the field span both columns of the 2-col grid; otherwise
// the field is half-width and pairs with its neighbour. File uploads are
// auto-wide because the picker chip looks bad cramped.
// Required = critical for finance / legal / tracking. Anything else is optional.
// Audit rationale (CHRO view):
//   · Identifiers (Serial, Vehicle No, Card No, SIM No) → required: tracking
//     breaks without them.
//   · Purchase Date + Cost → required for depreciation and finance reporting.
//   · Warranty / Insurance / Photos / Invoice / Password → optional: useful
//     but the asset can be created and used without them.
// asset_tag: a free-form identifier HR/admin types in by hand (a physical
// tag/sticker number, or whatever numbering scheme the org already uses) —
// distinct from the system's own auto-generated id and from serial_number
// (a manufacturer serial, Electronics-only). No format restriction, any
// category. This is what makes "which of the 100 laptops is with whom"
// answerable at a glance without opening each row.
const ASSET_TAG_FIELD = { key: 'asset_tag', label: 'Asset ID', type: 'text', required: true, top: true, placeholder: 'e.g. ZUCOL-LAP-014' };

const ASSET_FORM_CONFIG = {
  Electronics: [
    ASSET_TAG_FIELD,
    { key: 'cat',             label: 'Asset Type',          type: 'subtype', required: true,  wide: true },
    { key: 'brand',           label: 'Brand',               type: 'text',    required: true,  top: true, placeholder: 'Dell' },
    { key: 'model',           label: 'Model',               type: 'text',    required: true,  top: true, placeholder: 'Latitude 5540' },
    { key: 'serial_number',   label: 'Serial No',           type: 'text',    required: true,  top: true, placeholder: 'DL-2024-0001', wide: true },
    { key: 'purchase_date',   label: 'Purchase Date',       type: 'date',    required: true,  top: true },
    { key: 'purchase_cost',   label: 'Cost (₹)',            type: 'number',  required: true,  top: true, placeholder: '85000' },
    { key: 'warranty_expiry', label: 'Warranty Expiry',     type: 'date',    top: true },
    { key: 'ownership',       label: 'Rent / Fixed',        type: 'select',  required: true,  options: [{ v: 'Fixed', l: 'Fixed' }, { v: 'Rent', l: 'Rent' }] },
    { key: 'password',        label: 'Password',            type: 'text',    wide: true },
    { key: 'invoice',         label: 'Upload Asset Invoice',type: 'file' },
  ],
  'Phone No': [
    ASSET_TAG_FIELD,
    { key: 'cat',           label: 'Asset Type',     type: 'subtype', required: true,  wide: true },
    { key: 'company',       label: 'Company',        type: 'text',    required: true,  placeholder: 'Airtel / Jio / Vi' },
    { key: 'sim_no',        label: 'SIM No',         type: 'text',    required: true },
    { key: 'purchase_date', label: 'Purchase Date',  type: 'date', required: true, top: true, wide: true },
  ],
  Vehicles: [
    ASSET_TAG_FIELD,
    { key: 'cat',             label: 'Asset Type',          type: 'subtype', required: true, wide: true },
    { key: 'brand',           label: 'Brand',               type: 'text',    required: true, top: true, placeholder: 'Maruti Suzuki' },
    { key: 'vehicle_no',      label: 'Vehicle No',          type: 'text',    required: true, placeholder: 'HR 26 AB 1234' },
    { key: 'purchase_date',   label: 'Purchase Date',       type: 'date',    required: true, top: true },
    { key: 'purchase_cost',   label: 'Cost (₹)',            type: 'number',  required: true, top: true },
    { key: 'invoice',         label: 'Upload Asset Invoice',type: 'file' },
    { key: 'vehicle_paper',   label: 'Upload Asset Paper',  type: 'file' },
    { key: 'insurance',       label: 'Upload Asset Insurance', type: 'file' },
  ],
  Cards: [
    ASSET_TAG_FIELD,
    { key: 'cat',           label: 'Asset Type',     type: 'subtype', required: true, wide: true },
    { key: 'card_no',       label: 'Card No',        type: 'text',    required: true },
    { key: 'purchase_date', label: 'Purchase Date',  type: 'date',    required: true, top: true },
    { key: 'invoice',       label: 'Upload Invoice', type: 'file' },
  ],
  Furniture: [
    ASSET_TAG_FIELD,
    { key: 'cat',           label: 'Asset Type',           type: 'subtype', required: true, wide: true },
    { key: 'purchase_date', label: 'Purchase Date',        type: 'date',    required: true, top: true, wide: true },
    { key: 'invoice',       label: 'Upload Invoice',       type: 'file' },
    { key: 'photo',         label: 'Upload Photo of Asset',type: 'file' },
  ],
  // "Others" has only the OTHER subtype in the DB, so the dropdown adds no
  // value — let admin type the asset type as free text instead. The submit
  // path resolves category_code to the OTHER subtype automatically.
  Others: [
    ASSET_TAG_FIELD,
    { key: 'asset_name',    label: 'Asset Name',     type: 'text', required: true, wide: true, placeholder: 'Espresso Machine' },
    { key: 'asset_type',    label: 'Asset Type',     type: 'text', required: true, placeholder: 'Appliance / Tool / Misc' },
    { key: 'purchase_date', label: 'Purchase Date',  type: 'date', required: true, top: true },
  ],
};

// File upload row used inside AddAstModal — wraps a hidden <input type=file>
// with a click-to-pick label and a preview chip once a file has been chosen.
const AssetFileField = ({ field, value, onChange }) => {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState(null);

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setErr(null); setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('key', field.key);
      const r = await api.upload('/api/assets/upload-file', fd);
      if (r) onChange({ path: r.path, name: r.name });
    } catch (ex) { setErr(ex.message || 'Upload failed'); }
    finally { setUploading(false); if (inputRef.current) inputRef.current.value = ''; }
  };

  return (
    <div>
      <div className="text-[14px] text-t2 mb-[3px]">{field.label}</div>
      <input ref={inputRef} type="file" className="hidden" onChange={onPick}
             accept="image/*,application/pdf" />
      {value?.path ? (
        <div className="flex items-center gap-2 px-2 py-[7px] bg-bg2 border border-b0 rounded-lg">
          <FileText size={14} color={K.p} />
          <a href={`${BASE}${value.path}`} target="_blank" rel="noreferrer" className="flex-1 text-[14px] text-p font-semibold no-underline truncate">{value.name}</a>
          <button onClick={() => onChange(null)} className="bg-transparent border-0 text-t3 cursor-pointer text-[14px]"><X size={12} /></button>
        </div>
      ) : (
        <button type="button" onClick={() => inputRef.current?.click()} disabled={uploading}
                className="w-full px-2 py-[7px] bg-bg2 border border-dashed border-b1 rounded-lg text-[14px] text-t2 cursor-pointer flex items-center justify-center gap-2 hover:border-p hover:text-p">
          <Upload size={12} /> {uploading ? 'Uploading…' : 'Choose file (PDF or image, ≤10MB)'}
        </button>
      )}
      {err && <div className="text-[14px] text-r mt-1">{err}</div>}
    </div>
  );
};

// Map an action verb to an icon + colour. Anything not in this map falls back
// to the generic Activity dot — keeps the list readable when we add new
// actions later without forcing a UI change.
const ACTION_META = {
  created:  { icon: Plus,           color: '#2BD49D', verb: 'Created' },
  updated:  { icon: Edit3,          color: '#5AA0F0', verb: 'Updated' },
  assigned: { icon: UserCheck,      color: '#5F6CF0', verb: 'Assigned' },
  returned: { icon: ArrowRightLeft, color: '#F0BE2E', verb: 'Returned' },
};

const fmtRelTime = (iso) => {
  if (!iso) return '';
  const t = new Date(iso).getTime();
  if (isNaN(t)) return iso;
  const s = Math.round((Date.now() - t) / 1000);
  if (s < 60) return s + 's ago';
  if (s < 3600) return Math.round(s / 60) + 'm ago';
  if (s < 86400) return Math.round(s / 3600) + 'h ago';
  if (s < 604800) return Math.round(s / 86400) + 'd ago';
  return fmtDMY(iso);
};

const summariseLogDetails = (action, d = {}) => {
  if (action === 'created') {
    const bits = [];
    if (d.brand || d.model) bits.push(`${d.brand || ''} ${d.model || ''}`.trim());
    if (d.serial_number) bits.push(`SN: ${d.serial_number}`);
    return bits.join(' · ');
  }
  if (action === 'assigned') {
    return `to ${d.employee_name || d.employee_id || 'employee'}`;
  }
  if (action === 'returned') {
    return `condition: ${d.condition || '—'}${d.new_status ? ` (${d.new_status})` : ''}`;
  }
  if (action === 'updated' && d.changes) {
    const keys = Object.keys(d.changes).filter(k => k !== 'attributes');
    const human = keys.map(k => {
      const c = d.changes[k];
      if (c?.from !== undefined && c?.to !== undefined) return `${k.replace(/_/g, ' ')}: ${fmtDDMMYY(c.from) || '—'} → ${fmtDDMMYY(c.to) || '—'}`;
      return k.replace(/_/g, ' ');
    });
    if (d.changes.attributes) human.push('details');
    return human.join(' · ');
  }
  return '';
};

const AssetActivityLog = ({ assetId }) => {
  const [entries, setEntries] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!assetId) return;
    let alive = true;
    api.get(`/api/assets/${assetId}/audit-log`)
       .then(d => { if (alive) setEntries(Array.isArray(d) ? d : []); })
       .catch(e => { if (alive) setErr(e.message || 'Failed to load log'); });
    return () => { alive = false; };
  }, [assetId]);

  if (err) return <div className="text-[14px] text-r">{err}</div>;
  if (entries === null) return <div className="text-[14px] text-t3">Loading activity…</div>;
  if (!entries.length) return <div className="text-[14px] text-t3">No activity recorded yet.</div>;

  return (
    <div className="space-y-[6px]">
      {entries.map(e => {
        const meta = ACTION_META[e.action] || { icon: Activity, color: K.t3, verb: e.action };
        const Icon = meta.icon;
        const summary = summariseLogDetails(e.action, e.details);
        return (
          <div key={e.id} className="flex items-start gap-2 py-[6px]" style={{ borderBottom: `1px solid ${K.b0}30` }}>
            <div className="flex items-center justify-center w-6 h-6 rounded-full flex-shrink-0 mt-[1px]"
                 style={{ background: `${meta.color}1A`, border: `1px solid ${meta.color}40` }}>
              <Icon size={11} color={meta.color} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[14px] text-t0">
                <span className="font-semibold">{meta.verb}</span>
                <span className="text-t3"> by </span>
                <span className="font-semibold">{e.actorName || 'Unknown'}</span>
              </div>
              {summary && <div className="text-[14px] text-t2 truncate" title={summary}>{summary}</div>}
              <div className="text-[12px] text-t3">{fmtRelTime(e.at)}</div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

// State badge for assignment lifecycle. Pure helper so My Assets, the
// inventory list, and the detail drawer all label the same state the same
// way — avoids "active" in one place and "Assigned" in another.
const ASSIGN_STATE_META = {
  pending_ack:    { label: 'Awaiting Acknowledgement', color: '#F0BE2E' },
  active:         { label: 'Acknowledged',             color: '#2BD49D' },
  pending_return: { label: 'Return in Progress',       color: '#5AA0F0' },
  returned:       { label: 'Returned',                 color: '#8A92A6' },
  rejected:       { label: 'Rejected',                 color: '#F06268' },
  cancelled:      { label: 'Cancelled',                color: '#8A92A6' },
};
const AssignStateBadge = ({ state }) => {
  if (!state) return null;
  const m = ASSIGN_STATE_META[state] || { label: state, color: '#8A92A6' };
  return <Bg c={m.color} sm>{m.label}</Bg>;
};

// HR-side: assign an asset to an employee. Replaces the old window.prompt()
// flow — picks an employee with type-ahead search, captures handover
// condition, optional note, optional expected return date.
const AssignAssetModal = ({ assetId, asset, employees = [], onClose, onSaved }) => {
  const [search, setSearch] = useState('');
  const [picked, setPicked] = useState(null); // { id, nm }
  const [condition, setCondition] = useState(asset?.condition || 'Good');
  const [note, setNote] = useState('');
  const [expReturn, setExpReturn] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const matches = !search ? [] : employees
    .filter(e => e.st === 'Active')
    .filter(e => {
      const s = search.toLowerCase();
      return (e.nm || '').toLowerCase().includes(s) || (e.id || '').toLowerCase().includes(s);
    })
    .slice(0, 8);

  const submit = async () => {
    if (!picked) return setErr('Pick an employee');
    setSaving(true); setErr('');
    try {
      await api.post(`/api/assets/${assetId}/assign`, {
        employee_id: picked.id,
        condition,
        note: note || null,
        expected_return_date: expReturn || null,
      });
      notify(`Asset assigned to ${picked.nm}`, { title: 'Saved', variant: 'success' });
      onSaved?.();
      onClose();
    } catch (e) {
      setErr(e.message || 'Assign failed');
      notify(e.message || 'Assign failed', { title: 'Error', variant: 'error' });
    }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 animate-fadeIn bg-black/50 z-[210] flex items-center justify-center p-3" onMouseDown={onClose}>
      <div className="animate-modalIn bg-bg1 border border-b1 rounded-2xl p-5 w-full max-w-[712px] max-h-[90vh] overflow-auto" onMouseDown={e => e.stopPropagation()}>
        <div className="flex justify-between items-start mb-4">
          <div>
            <div className="text-[18px] font-extrabold text-t0">Assign Asset</div>
            <div className="text-[14px] text-t3 mt-[2px]">{`${asset?.brand || ''} ${asset?.model || ''}`.trim() || asset?.catNm} · {assetId}</div>
          </div>
          <button onClick={onClose} className="bg-transparent border-0 text-t3 cursor-pointer"><X size={18}/></button>
        </div>

        <div className="mb-3">
          <div className="text-[14px] text-t2 mb-[3px]">Employee *</div>
          {picked ? (
            <div className="flex items-center gap-2 p-2 bg-bg2 border border-b0 rounded-lg">
              <UserCheck size={14} color={K.g} />
              <div className="flex-1">
                <div className="text-[14px] font-semibold text-t0">{picked.nm}</div>
                <div className="text-[12px] text-t3 font-mono">{picked.id}</div>
              </div>
              <button onClick={() => { setPicked(null); setSearch(''); }} className="bg-transparent border-0 text-t3 cursor-pointer text-[14px]"><X size={12}/></button>
            </div>
          ) : (
            <>
              <Inp placeholder="Search by name or employee ID…" value={search} onChange={setSearch} />
              {matches.length > 0 && <div className="mt-1 max-h-[180px] overflow-auto border border-b0 rounded-lg bg-bg2">
                {matches.map(e => (
                  <div key={e.id} onClick={() => { setPicked({ id: e.id, nm: e.nm }); setSearch(''); }}
                       className="flex items-center gap-2 p-2 cursor-pointer hover:bg-sh">
                    <Av nm={e.nm} sz={20} />
                    <div className="flex-1 min-w-0">
                      <div className="text-[14px] font-semibold text-t0 truncate">{e.nm}</div>
                      <div className="text-[12px] text-t3 font-mono">{e.id}</div>
                    </div>
                  </div>
                ))}
              </div>}
              {search && matches.length === 0 && <div className="text-[14px] text-t3 mt-1">No active employees match.</div>}
            </>
          )}
        </div>

        <div className="grid grid-cols-2 gap-3 mb-3">
          <div>
            <div className="text-[14px] text-t2 mb-[3px]">Condition at Handover *</div>
            <Sl options={[{ v: 'New', l: 'New' }, { v: 'Good', l: 'Good' }, { v: 'Fair', l: 'Fair' }]} value={condition} onChange={setCondition} />
          </div>
          <div>
            <div className="text-[14px] text-t2 mb-[3px]">Expected Return</div>
            <Inp type="date" value={expReturn} onChange={setExpReturn} />
          </div>
        </div>

        <div className="mb-3">
          <div className="text-[14px] text-t2 mb-[3px]">HR Note (optional)</div>
          <textarea value={note} onChange={e => setNote(e.target.value)} placeholder="Joining kit / project loan / replacement for AST0008…"
                    className="w-full min-h-[50px] p-2 bg-bg2 border border-b0 rounded-lg text-t0 text-[14px] outline-none resize-y" />
        </div>

        <div className="text-[14px] text-t3 mb-3 p-2 bg-bg2 rounded-lg border border-b0">
          The employee must acknowledge before this assignment becomes active. They'll see it in their My Assets tab.
        </div>

        {err && <div className="text-[14px] text-r mb-2">{err}</div>}
        <div className="flex gap-2">
          <Bt full onClick={submit} disabled={saving}>{saving ? 'Assigning…' : 'Send to Employee'}</Bt>
          <Bt v="secondary" full onClick={onClose}>Cancel</Bt>
        </div>
      </div>
    </div>
  );
};

// Employee-side: acknowledge a pending asset. Captures a typed-name signature
// (server checks that the signature contains the employee's first name).
const AcknowledgeModal = ({ assetId, asset, employeeName, onClose, onSaved }) => {
  const [signature, setSignature] = useState('');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const submit = async () => {
    if (!signature.trim()) return setErr('Type your full name to confirm');
    setSaving(true); setErr('');
    try {
      await api.post(`/api/assets/${assetId}/acknowledge`, { signature: signature.trim(), note: note || null });
      notify('Asset acknowledged', { title: 'Saved', variant: 'success' });
      onSaved?.();
      onClose();
    } catch (e) {
      setErr(e.message || 'Acknowledge failed');
      notify(e.message || 'Acknowledge failed', { title: 'Error', variant: 'error' });
    }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 animate-fadeIn bg-black/50 z-[210] flex items-center justify-center p-3" onMouseDown={onClose}>
      <div className="animate-modalIn bg-bg1 border border-b1 rounded-2xl p-5 w-full max-w-[684px]" onMouseDown={e => e.stopPropagation()}>
        <div className="flex justify-between items-start mb-3">
          <div className="text-[17px] font-extrabold text-t0">Acknowledge Receipt</div>
          <button onClick={onClose} className="bg-transparent border-0 text-t3 cursor-pointer"><X size={18}/></button>
        </div>
        <div className="mb-3 p-3 bg-bg2 border border-b0 rounded-lg">
          <div className="text-[14px] text-t3 uppercase">Asset</div>
          <div className="text-[15px] font-bold text-t0">{`${asset?.brand || ''} ${asset?.model || ''}`.trim() || asset?.catNm}</div>
          <div className="text-[14px] text-t3 font-mono">{assetId}</div>
          {asset?.assignedCondition && <div className="text-[14px] text-t2 mt-1">Condition at handover: <span className="font-semibold">{asset.assignedCondition}</span></div>}
        </div>
        <div className="text-[14px] text-t2 mb-3">
          By acknowledging, you confirm receipt of this asset in the recorded condition. You become responsible for it until it's returned and re-inspected.
        </div>
        <div className="mb-3">
          <div className="text-[14px] text-t2 mb-[3px]">Type your full name to sign *</div>
          <Inp placeholder={employeeName || 'Your full name'} value={signature} onChange={setSignature} />
        </div>
        <div className="mb-3">
          <div className="text-[14px] text-t2 mb-[3px]">Note (optional)</div>
          <textarea value={note} onChange={e => setNote(e.target.value)} placeholder="Anything HR should know about the handover…"
                    className="w-full min-h-[44px] p-2 bg-bg2 border border-b0 rounded-lg text-t0 text-[14px] outline-none resize-y" />
        </div>
        {err && <div className="text-[14px] text-r mb-2">{err}</div>}
        <div className="flex gap-2">
          <Bt v="success" full onClick={submit} disabled={saving}>{saving ? 'Acknowledging…' : 'Acknowledge & Accept'}</Bt>
          <Bt v="secondary" full onClick={onClose}>Cancel</Bt>
        </div>
      </div>
    </div>
  );
};

// Employee-side: refuse a pending asset, capturing a reason that's surfaced
// to HR via in-app notification + audit log.
const RejectAssignmentModal = ({ assetId, onClose, onSaved }) => {
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const submit = async () => {
    if (!reason.trim()) return setErr('Reason is required');
    setSaving(true); setErr('');
    try {
      await api.post(`/api/assets/${assetId}/reject-assignment`, { reason: reason.trim() });
      notify('Assignment rejected', { title: 'Saved', variant: 'success' });
      onSaved?.();
      onClose();
    } catch (e) {
      setErr(e.message || 'Reject failed');
      notify(e.message || 'Reject failed', { title: 'Error', variant: 'error' });
    }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 animate-fadeIn bg-black/50 z-[210] flex items-center justify-center p-3" onMouseDown={onClose}>
      <div className="animate-modalIn bg-bg1 border border-b1 rounded-2xl p-5 w-full max-w-[628px]" onMouseDown={e => e.stopPropagation()}>
        <div className="flex justify-between items-start mb-3">
          <div className="text-[17px] font-extrabold text-t0">Reject Asset Assignment</div>
          <button onClick={onClose} className="bg-transparent border-0 text-t3 cursor-pointer"><X size={18}/></button>
        </div>
        <div className="text-[14px] text-t2 mb-3">HR will be notified with your reason and the asset returns to the available pool.</div>
        <div className="mb-3">
          <div className="text-[14px] text-t2 mb-[3px]">Reason *</div>
          <textarea value={reason} onChange={e => setReason(e.target.value)} placeholder="Wrong asset / damaged on receipt / not needed for my role…"
                    className="w-full min-h-[60px] p-2 bg-bg2 border border-b0 rounded-lg text-t0 text-[14px] outline-none resize-y" />
        </div>
        {err && <div className="text-[14px] text-r mb-2">{err}</div>}
        <div className="flex gap-2">
          <Bt v="danger" full onClick={submit} disabled={saving}>{saving ? 'Rejecting…' : 'Reject'}</Bt>
          <Bt v="secondary" full onClick={onClose}>Cancel</Bt>
        </div>
      </div>
    </div>
  );
};

// HR-side: complete the return inspection. Final condition + optional damage
// amount + notes. Can also dispose if the asset is end-of-life.
const CompleteReturnModal = ({ assetId, currentEmployee, onClose, onSaved }) => {
  const [finalCondition, setFinalCondition] = useState('Good');
  const [accessoriesOk, setAccessoriesOk] = useState('yes');
  const [damageAmount, setDamageAmount] = useState('');
  const [inspectionNotes, setInspectionNotes] = useState('');
  const [dispose, setDispose] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const submit = async () => {
    setSaving(true); setErr('');
    try {
      await api.post(`/api/assets/${assetId}/complete-return`, {
        final_condition: finalCondition,
        damage_amount: damageAmount ? parseFloat(damageAmount) : null,
        dispose,
        inspection: {
          accessories_ok: accessoriesOk === 'yes',
          notes: inspectionNotes || null,
          inspected_at: new Date().toISOString(),
        },
      });
      notify(dispose ? 'Asset returned and marked disposed' : 'Return completed', { title: 'Saved', variant: 'success' });
      onSaved?.();
      onClose();
    } catch (e) {
      setErr(e.message || 'Return failed');
      notify(e.message || 'Return failed', { title: 'Error', variant: 'error' });
    }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 animate-fadeIn bg-black/50 z-[210] flex items-center justify-center p-3" onMouseDown={onClose}>
      <div className="animate-modalIn bg-bg1 border border-b1 rounded-2xl p-5 w-full max-w-[684px]" onMouseDown={e => e.stopPropagation()}>
        <div className="flex justify-between items-start mb-3">
          <div className="text-[17px] font-extrabold text-t0">Complete Return Inspection</div>
          <button onClick={onClose} className="bg-transparent border-0 text-t3 cursor-pointer"><X size={18}/></button>
        </div>
        {currentEmployee && <div className="text-[14px] text-t2 mb-3">Returning from <span className="font-semibold">{currentEmployee}</span></div>}

        <div className="grid grid-cols-2 gap-3 mb-3">
          <div>
            <div className="text-[14px] text-t2 mb-[3px]">Final Condition *</div>
            <Sl options={[{ v: 'New', l: 'New' }, { v: 'Good', l: 'Good' }, { v: 'Fair', l: 'Fair' }, { v: 'Damaged', l: 'Damaged' }]} value={finalCondition} onChange={setFinalCondition} />
          </div>
          <div>
            <div className="text-[14px] text-t2 mb-[3px]">All accessories present?</div>
            <Sl options={[{ v: 'yes', l: 'Yes' }, { v: 'no', l: 'No / partial' }]} value={accessoriesOk} onChange={setAccessoriesOk} />
          </div>
        </div>

        <div className="mb-3">
          <div className="text-[14px] text-t2 mb-[3px]">Damage Charge (₹, if any)</div>
          <Inp type="number" placeholder="0" value={damageAmount} onChange={setDamageAmount} />
        </div>

        <div className="mb-3">
          <div className="text-[14px] text-t2 mb-[3px]">Inspection Notes</div>
          <textarea value={inspectionNotes} onChange={e => setInspectionNotes(e.target.value)} placeholder="Scratch on lid, charger missing, etc."
                    className="w-full min-h-[50px] p-2 bg-bg2 border border-b0 rounded-lg text-t0 text-[14px] outline-none resize-y" />
        </div>

        <label className="flex items-center gap-2 mb-3 cursor-pointer">
          <input type="checkbox" checked={dispose} onChange={e => setDispose(e.target.checked)} />
          <span className="text-[14px] text-t1">Mark as disposed (end-of-life, write off the asset)</span>
        </label>

        <div className="text-[14px] text-t3 mb-3 p-2 bg-bg2 rounded-lg border border-b0">
          {dispose
            ? 'Asset will be marked Disposed and removed from active inventory.'
            : finalCondition === 'Damaged'
              ? 'Damaged assets move to In Repair status.'
              : 'Asset returns to Available pool and can be reassigned.'}
        </div>

        {err && <div className="text-[14px] text-r mb-2">{err}</div>}
        <div className="flex gap-2">
          <Bt v="success" full onClick={submit} disabled={saving}>{saving ? 'Saving…' : 'Complete Return'}</Bt>
          <Bt v="secondary" full onClick={onClose}>Cancel</Bt>
        </div>
      </div>
    </div>
  );
};

// Scrap / write-off an asset. Distinct from a return inspection — this is for
// assets that won't be coming back into circulation (irreparable damage, end
// of life, sold to a vendor, lost, stolen). Captures method + reason as a
// hard requirement; recovery value and proof file are optional.
const DISPOSAL_METHODS = [
  { v: 'Scrapped', l: 'Scrapped (end of life / irreparable)' },
  { v: 'Sold',     l: 'Sold to vendor / employee' },
  { v: 'Donated',  l: 'Donated' },
  { v: 'Lost',     l: 'Lost' },
  { v: 'Stolen',   l: 'Stolen (FIR filed)' },
];

const ScrapAssetModal = ({ assetId, asset, onClose, onSaved }) => {
  const [method, setMethod] = useState('Scrapped');
  const [reason, setReason] = useState('');
  const [value, setValue] = useState('');
  const [proof, setProof] = useState(null);
  const [confirm, setConfirm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const onPickFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setErr(null); setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', f);
      fd.append('key', 'invoice'); // reuse the existing whitelisted key for proof
      const r = await api.upload('/api/assets/upload-file', fd);
      if (r) setProof({ path: r.path, name: r.name });
    } catch (ex) {
      setErr(ex.message || 'Upload failed');
      notify(ex.message || 'Upload failed', { title: 'Error', variant: 'error' });
    }
    finally { setUploading(false); if (fileInputRef.current) fileInputRef.current.value = ''; }
  };

  const submit = async () => {
    if (reason.trim().length < 5) return setErr('Reason is required (min 5 characters)');
    if (!confirm) return setErr('Please confirm — disposal is a financial event');
    setSaving(true); setErr('');
    try {
      await api.post(`/api/assets/${assetId}/dispose`, {
        method, reason: reason.trim(),
        value: value ? parseFloat(value) : null,
        proof_path: proof?.path || null,
      });
      notify(`Asset marked as ${method}`, { title: 'Saved', variant: 'success' });
      onSaved?.();
      onClose();
    } catch (e) {
      setErr(e.message || 'Disposal failed');
      notify(e.message || 'Disposal failed', { title: 'Error', variant: 'error' });
    }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 animate-fadeIn bg-black/55 z-[210] flex items-center justify-center p-3" onMouseDown={onClose}>
      <div className="animate-modalIn bg-bg1 border border-b1 rounded-2xl p-5 w-full max-w-[712px] max-h-[90vh] overflow-auto" onMouseDown={e => e.stopPropagation()}>
        <div className="flex justify-between items-start mb-3 gap-3">
          <div className="flex-1 min-w-0">
            <div className="text-[18px] font-extrabold text-r flex items-center gap-2"><AlertTriangle size={18} color={K.r} /> Scrap / Write Off Asset</div>
            <div className="text-[14px] text-t3 mt-[2px] truncate">{`${asset?.brand || ''} ${asset?.model || ''}`.trim() || asset?.catNm} · {assetId}</div>
          </div>
          <button onClick={onClose} className="bg-transparent border-0 text-t3 cursor-pointer p-1"><X size={18}/></button>
        </div>

        <div className="text-[14px] text-r mb-3 p-2 bg-rb border border-rd rounded-lg">
          Disposal is a financial write-off and a permanent state. Any active assignment will be auto-closed. An audit log entry will be created with your name and timestamp.
        </div>

        <div className="mb-3">
          <div className="text-[14px] text-t2 mb-[3px]">Disposal Method *</div>
          <Sl options={DISPOSAL_METHODS} value={method} onChange={setMethod} />
        </div>

        <div className="mb-3">
          <div className="text-[14px] text-t2 mb-[3px]">Reason *</div>
          <textarea value={reason} onChange={e => setReason(e.target.value)}
                    placeholder={method === 'Lost' ? 'When and where last seen, who held it…'
                                : method === 'Stolen' ? 'FIR no., date, summary of incident…'
                                : method === 'Sold' ? 'Buyer, sale terms, why selling…'
                                : 'Specifics — repair quote vs replacement cost, age, etc.'}
                    className="w-full min-w-0 min-h-[60px] p-2 bg-bg2 border border-b0 rounded-lg text-t0 text-[14px] outline-none resize-y" />
        </div>

        <div className="mb-3">
          <div className="text-[14px] text-t2 mb-[3px]">
            {method === 'Sold' ? 'Sale Proceeds (₹)'
              : method === 'Scrapped' ? 'Scrap Value Recovered (₹)'
              : 'Recovery / Write-off Amount (₹)'}
          </div>
          <Inp type="number" placeholder="0" value={value} onChange={setValue} />
        </div>

        <div className="mb-3">
          <div className="text-[14px] text-t2 mb-[3px]">
            Proof Document {method === 'Stolen' ? '(FIR copy)' : method === 'Sold' ? '(invoice/receipt)' : '(optional)'}
          </div>
          <input ref={fileInputRef} type="file" className="hidden" onChange={onPickFile} accept="image/*,application/pdf" />
          {proof?.path ? (
            <div className="flex items-center gap-2 px-2 py-[7px] bg-bg2 border border-b0 rounded-lg">
              <FileText size={14} color={K.p} />
              <a href={`${BASE}${proof.path}`} target="_blank" rel="noreferrer" className="flex-1 text-[14px] text-p font-semibold no-underline truncate">{proof.name}</a>
              <button onClick={() => setProof(null)} className="bg-transparent border-0 text-t3 cursor-pointer text-[14px]"><X size={12} /></button>
            </div>
          ) : (
            <button type="button" onClick={() => fileInputRef.current?.click()} disabled={uploading}
                    className="w-full px-2 py-[7px] bg-bg2 border border-dashed border-b1 rounded-lg text-[14px] text-t2 cursor-pointer flex items-center justify-center gap-2 hover:border-p hover:text-p">
              <Upload size={12} /> {uploading ? 'Uploading…' : 'Choose file (PDF or image, ≤10MB)'}
            </button>
          )}
        </div>

        <label className="flex items-start gap-2 mb-3 cursor-pointer">
          <input type="checkbox" checked={confirm} onChange={e => setConfirm(e.target.checked)} className="mt-1" />
          <span className="text-[14px] text-t1">I confirm this asset is being disposed of per company policy and I'm authorised to record this write-off.</span>
        </label>

        {err && <div className="text-[14px] text-r mb-2">{err}</div>}
        <div className="flex gap-2">
          <Bt v="danger" full onClick={submit} disabled={saving || !confirm}>{saving ? 'Disposing…' : 'Confirm Disposal'}</Bt>
          <Bt v="secondary" full onClick={onClose}>Cancel</Bt>
        </div>
      </div>
    </div>
  );
};

// HR-side: approve an asset request by picking a specific Available asset
// from inventory (centre-scoped server-side — there is intentionally no
// "show all centres" toggle). Falls back to "Awaiting Inventory" when no
// matching stock exists yet.
const ApproveRequestModal = ({ request, onClose, onSaved }) => {
  const [stock, setStock] = useState(null); // null = loading
  const [pickedId, setPickedId] = useState('');
  const [awaiting, setAwaiting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const [photo, setPhoto] = useState(null); // {path, name} — required once an asset is actually picked

  useEffect(() => {
    let alive = true;
    api.get(`/api/assets/inventory?status=Available&category=${encodeURIComponent(request.cat || '')}`)
       .then(d => { if (alive) setStock(Array.isArray(d) ? d : []); })
       .catch(e => { if (alive) { setStock([]); setErr(e.message || 'Failed to load stock'); } });
    return () => { alive = false; };
  }, [request.cat]);

  // Backend already centre-scopes inventory to the user's accessible offices,
  // but we further narrow to the requesting employee's own office so the
  // chosen asset and the holder are co-located. all-centre HR can still pick
  // outside that office by toggling — actually no, per CHRO direction: no
  // toggle. Keep it strict.
  const matching = (stock || []).filter(a => {
    if (request.empOfficeId && a.office) {
      // office is the city string; match on office_id when available
      return !a.office_id || !request.empOfficeId || a.office_id === request.empOfficeId;
    }
    return true;
  });

  const submit = async () => {
    setErr('');
    if (!awaiting && !pickedId) return setErr('Pick an available asset, or check "Awaiting Inventory"');
    if (!awaiting && pickedId && !photo?.path) return setErr('Upload a handover photo before allotting');
    setSaving(true);
    try {
      await api.post(`/api/assets/requests/${request.id}/approve`, awaiting
        ? { awaiting_inventory: true }
        : { asset_id: pickedId, handover_photo: photo });
      notify(awaiting ? 'Request approved — awaiting inventory' : 'Request approved and asset assigned', { title: 'Saved', variant: 'success' });
      onSaved?.();
      onClose();
    } catch (e) {
      setErr(e.message || 'Approve failed');
      notify(e.message || 'Approve failed', { title: 'Error', variant: 'error' });
    }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 animate-fadeIn bg-black/55 z-[210] flex items-center justify-center p-3" onMouseDown={onClose}>
      <div className="animate-modalIn bg-bg1 border border-b1 rounded-2xl p-5 w-full max-w-[768px] max-h-[90vh] overflow-auto" onMouseDown={e => e.stopPropagation()}>
        <div className="flex justify-between items-start mb-3">
          <div className="flex-1 min-w-0">
            <div className="text-[17px] font-extrabold text-t0">Approve Asset Request</div>
            <div className="text-[14px] text-t3 mt-[2px]">{request.enm} · <span className="font-mono">{request.eid}</span></div>
            <div className="text-[14px] text-t2 mt-1">Wants: <span className="font-semibold">{request.catNm}</span> · "{request.reason}"</div>
          </div>
          <button onClick={onClose} className="bg-transparent border-0 text-t3 cursor-pointer p-1"><X size={18}/></button>
        </div>

        <div className="mb-3">
          <div className="text-[14px] text-t2 mb-[6px]">Pick from Available stock at the employee's centre</div>
          {stock === null ? (
            <div className="text-[14px] text-t3 p-3 bg-bg2 rounded-lg border border-b0">Loading inventory…</div>
          ) : matching.length === 0 ? (
            <div className="text-[14px] text-a p-3 bg-ab border border-ad rounded-lg flex items-start gap-2">
              <AlertTriangle size={12} className="mt-[2px]" />
              <span>No <span className="font-semibold">{request.catNm}</span> available at this centre. Check the box below to mark approved-but-awaiting-inventory.</span>
            </div>
          ) : (
            <div className="max-h-[260px] overflow-auto border border-b0 rounded-lg divide-y divide-b0">
              {matching.map(a => (
                <label key={a.id} className={`flex items-center gap-2 p-2 cursor-pointer ${pickedId === a.id ? 'bg-pg' : 'hover:bg-sh'}`}>
                  <input type="radio" name="pickAsset" checked={pickedId === a.id} onChange={() => { setPickedId(a.id); setAwaiting(false); }} />
                  <div className="flex-1 min-w-0">
                    <div className="text-[14px] font-semibold text-t0 truncate">{`${a.brand || ''} ${a.model || ''}`.trim() || a.catNm}</div>
                    <div className="text-[12px] text-t3 font-mono">{a.id}{a.serial ? ` · ${a.serial}` : ''}{a.office ? ` · ${a.office}` : ''}</div>
                  </div>
                  <Bg c={a.catClr || K.p} sm>{a.catNm}</Bg>
                </label>
              ))}
            </div>
          )}
        </div>

        {pickedId && !awaiting && (
          <div className="mb-3">
            {/* Same file field used everywhere else in Assets — a plain
                file input with no `capture` attribute already opens the
                device's native picker (Camera + Photo Library/Files) on
                mobile, so no extra work is needed for "camera or folder". */}
            <AssetFileField field={{ key: 'handover', label: 'Handover Photo (required)' }} value={photo} onChange={setPhoto} />
          </div>
        )}

        <label className="flex items-start gap-2 mb-3 cursor-pointer">
          <input type="checkbox" checked={awaiting} onChange={e => { setAwaiting(e.target.checked); if (e.target.checked) { setPickedId(''); setPhoto(null); } }} className="mt-1" />
          <span className="text-[14px] text-t1">Approve in principle — stock not available yet (mark as Awaiting Inventory; HR will fulfil when one comes back to Available).</span>
        </label>

        <div className="text-[14px] text-t3 mb-3 p-2 bg-bg2 rounded-lg border border-b0">
          {awaiting
            ? 'Request will sit in Awaiting Inventory until you fulfil it from a returned/new asset.'
            : pickedId
              ? 'Asset will be earmarked and pushed to the employee for acknowledgement (pending_ack). They become liable once they accept.'
              : 'Pick an asset above or check "Awaiting Inventory".'}
        </div>

        {err && <div className="text-[14px] text-r mb-2">{err}</div>}
        <div className="flex gap-2">
          <Bt v="success" full onClick={submit} disabled={saving || (!pickedId && !awaiting) || (pickedId && !awaiting && !photo?.path)}>{saving ? 'Saving…' : (awaiting ? 'Approve & Wait for Stock' : 'Approve & Assign')}</Bt>
          <Bt v="secondary" full onClick={onClose}>Cancel</Bt>
        </div>
      </div>
    </div>
  );
};

const RejectRequestModal = ({ requestId, onClose, onSaved }) => {
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const submit = async () => {
    if (reason.trim().length < 10) return setErr('Reason is required (min 10 characters)');
    setSaving(true); setErr('');
    try {
      await api.post(`/api/assets/requests/${requestId}/reject`, { reason: reason.trim() });
      notify('Request rejected', { title: 'Saved', variant: 'success' });
      onSaved?.();
      onClose();
    } catch (e) {
      setErr(e.message || 'Reject failed');
      notify(e.message || 'Reject failed', { title: 'Error', variant: 'error' });
    }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 animate-fadeIn bg-black/55 z-[210] flex items-center justify-center p-3" onMouseDown={onClose}>
      <div className="animate-modalIn bg-bg1 border border-b1 rounded-2xl p-5 w-full max-w-[656px]" onMouseDown={e => e.stopPropagation()}>
        <div className="flex justify-between items-start mb-3">
          <div className="text-[17px] font-extrabold text-r flex items-center gap-2"><X size={18} color={K.r} /> Reject Asset Request</div>
          <button onClick={onClose} className="bg-transparent border-0 text-t3 cursor-pointer p-1"><X size={18}/></button>
        </div>
        <div className="text-[14px] text-t2 mb-3">Employee will be notified with the reason. They can re-raise a fresh request if circumstances change.</div>
        <div className="mb-3">
          <div className="text-[14px] text-t2 mb-[3px]">Reason *</div>
          <textarea value={reason} onChange={e => setReason(e.target.value)}
                    placeholder="Budget freeze for Q1, please re-raise after April / role doesn't require this asset / already issued one this quarter…"
                    className="w-full min-w-0 min-h-[70px] p-2 bg-bg2 border border-b0 rounded-lg text-t0 text-[14px] outline-none resize-y" />
        </div>
        {err && <div className="text-[14px] text-r mb-2">{err}</div>}
        <div className="flex gap-2">
          <Bt v="danger" full onClick={submit} disabled={saving}>{saving ? 'Rejecting…' : 'Reject Request'}</Bt>
          <Bt v="secondary" full onClick={onClose}>Cancel</Bt>
        </div>
      </div>
    </div>
  );
};

export const AssetsPage = ({ astMy = [], astInv = [], astStats = null, astReqs = [], astCats = [], astTypeGroups = [], offices = [], employees = [], assignableEmployees = [], myProfile = null, hashQuery, role, onRefresh, can = () => true }) => {
  const [astTab, setAstTab] = useState(hashQuery?.tab === "requests" ? "requests" : "my");
  const [astDet, setAstDet] = useState(null);
  const [astAddModal, setAstAddModal] = useState(false);
  const [astReqModal, setAstReqModal] = useState(false);
  // Assignment lifecycle modals — each holds the asset object the action
  // applies to (or null when closed).
  const [assignModalFor, setAssignModalFor] = useState(null);
  const [ackModalFor, setAckModalFor] = useState(null);
  const [rejectModalFor, setRejectModalFor] = useState(null);
  const [completeReturnFor, setCompleteReturnFor] = useState(null);
  const [scrapModalFor, setScrapModalFor] = useState(null);
  const [purgeAsset, setPurgeAsset] = useState(null); // asset object being permanently deleted
  const [purgeConfirm, setPurgeConfirm] = useState('');
  const [purgeBusy, setPurgeBusy] = useState(false);
  const [approveReqFor, setApproveReqFor] = useState(null);
  const [rejectReqFor, setRejectReqFor] = useState(null);
  const [showDisposed, setShowDisposed] = useState(false);
  const [returningAstId, setReturningAstId] = useState(null); // My Assets → Return button, per-asset in-flight id
  const [astDetBusy, setAstDetBusy] = useState(''); // detail-drawer action in flight: '', 'cancel', 'return', 'restore', 'save'
  // When the user enables "show disposed", re-fetch the inventory locally
  // with include_disposed=1 — the parent-managed astInv only ever has
  // active assets. Off → fall back to the parent prop so refresh still
  // works through the existing onRefresh pipeline.
  const [disposedInv, setDisposedInv] = useState(null);
  useEffect(() => {
    if (!showDisposed) { setDisposedInv(null); return; }
    api.get('/api/assets/inventory?include_disposed=1').then(d => setDisposedInv(Array.isArray(d) ? d : (d?.data || []))).catch(() => setDisposedInv([]));
  }, [showDisposed]);
  const effInv = disposedInv ?? astInv;
  const [editVals, setEditVals] = useState({});
  // Inventory filters + pagination live at the top level (not inside the
  // conditional astTab==="inventory" render block) so usePagination runs
  // unconditionally on every render — calling it only while that tab is
  // active violates the Rules of Hooks and crashes on tab switch.
  const invSearch = editVals._invSearch || "";
  const invStFilter = editVals._invStFilter || "";
  const invCatFilter = editVals._invCatFilter || "";
  const invOfcFilter = editVals._invOfcFilter || "";
  const filteredInv = effInv.filter(a => {
    if (invSearch) { const s = invSearch.toLowerCase(); if (!(a.brand || "").toLowerCase().includes(s) && !(a.model || "").toLowerCase().includes(s) && !(a.serial || "").toLowerCase().includes(s) && !(a.id || "").toLowerCase().includes(s) && !(a.assetTag || "").toLowerCase().includes(s)) return false; }
    if (invStFilter && a.status !== invStFilter) return false;
    if (invCatFilter && a.cat !== invCatFilter) return false;
    if (invOfcFilter && a.office !== invOfcFilter) return false;
    return true;
  });
  const invPage = usePagination(filteredInv, 10);

  // Deep-link from a notification (#assets?tab=requests&id=AR000010) — jump
  // to the Requests tab and make sure the target row isn't hidden behind
  // whatever status filter chip happens to be selected.
  useEffect(() => {
    if (hashQuery?.tab === "requests") {
      setAstTab("requests");
      if (hashQuery.id) setEditVals(prev => ({ ...prev, _astFilter: null }));
    }
  }, [hashQuery]);

  const refreshAst=()=>{ onRefresh(); };
  const condClr=(c)=>c==="New"?K.g:c==="Good"?K.bl:c==="Fair"?K.a:c==="Damaged"?K.r:K.t3;
  const stClrA=(s)=>s==="Available"?K.g:s==="Assigned"?K.bl:s==="In Repair"?K.a:K.t3;
  // Inventory tab visibility (and its stats/Manage Asset actions) is now
  // driven by the granular permission catalog rather than a hardcoded role
  // list — default grants match the previous isAdmin/isAdminView role set
  // (admin, hr_head, centre_hr).
  const canViewInventory=can('assets','inventory',null,'view');

  const AddAstModal=()=>{
    const firstGroup = astTypeGroups[0];
    const subtypesIn = (gid) => astCats.filter(c => c.groupId === gid);
    const [groupId, setGroupId] = useState(firstGroup?.id || "");
    const [vals, setVals] = useState({}); // per-field values, keyed by field.key
    const [office, setOffice] = useState("1");
    const [notes, setNotes] = useState("");
    const [savedId, setSavedId] = useState(null);
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState("");

    const group = astTypeGroups.find(g => g.id === groupId);
    const fields = (group && ASSET_FORM_CONFIG[group.name]) || [];
    const subtypes = subtypesIn(groupId);

    // When group changes, reset the form state for that category (default the
    // sub-type to the first available so the dropdown has a real selection).
    const onPickGroup = (gid) => {
      const id = parseInt(gid) || "";
      setGroupId(id);
      const subs = subtypesIn(id);
      const init = {};
      const cfg = (astTypeGroups.find(g => g.id === id) && ASSET_FORM_CONFIG[astTypeGroups.find(g => g.id === id).name]) || [];
      for (const f of cfg) if (f.key === 'cat') init.cat = subs[0]?.cd || "";
      setVals(init);
      setErr("");
    };

    // Init defaults on first render
    React.useEffect(() => { onPickGroup(firstGroup?.id || ""); /* eslint-disable-next-line */ }, []);

    const setField = (k, v) => setVals(prev => ({ ...prev, [k]: v }));

    const doSubmit = async () => {
      setErr("");
      if (!groupId) return setErr("Please pick an asset type");
      // Required fields
      for (const f of fields) {
        if (f.required && (vals[f.key] === undefined || vals[f.key] === "" || vals[f.key] === null)) {
          return setErr(`${f.label} is required`);
        }
      }
      if (!office) return setErr("Centre / Location is required");

      // Build payload — split between top-level columns and attributes JSONB.
      const body = { office_id: parseInt(office) || null, notes: notes || null };
      const attributes = {};
      for (const f of fields) {
        const v = vals[f.key];
        if (v === undefined || v === "" || v === null) continue;
        if (f.key === 'cat') { body.category_code = v; continue; }
        if (f.top) {
          if (f.type === 'number') body[f.key] = parseFloat(v) || 0;
          else body[f.key] = v;
        } else {
          attributes[f.key] = v;
        }
      }

      // Some categories (Others) skip the subtype dropdown — auto-resolve
      // category_code to any subtype under the chosen group so the FK is
      // satisfied. The free-text "asset_type" the user typed lives in attributes.
      if (!body.category_code) {
        const fallback = astCats.find(c => c.groupId === groupId);
        if (!fallback) { setSaving(false); return setErr(`No asset types defined under "${group?.name}". Add one in Settings → Assets first.`); }
        body.category_code = fallback.cd;
      }

      body.attributes = attributes;

      setSaving(true);
      try {
        const r = await api.post("/api/assets", body);
        setSavedId(r.id);
      } catch (e) {
        setErr(e.message || "Error");
        notify(e.message || 'Failed to add asset', { title: 'Error', variant: 'error' });
      }
      finally { setSaving(false); }
    };

    const close = () => { setAstAddModal(false); if (savedId) refreshAst(); };

    // Render the post-create confirmation so the user sees the generated ID
    // (otherwise the modal vanishes and the new ID is buried in the table).
    if (savedId) {
      return <div className="fixed inset-0 animate-fadeIn bg-black/35 z-[200] flex items-center justify-center p-3" onMouseDown={close}>
        <div className="bg-bg1 border border-b1 rounded-2xl p-6 w-full max-w-[420px] text-center" onMouseDown={e=>e.stopPropagation()}>
          <CheckCircle size={32} color={K.g} className="mx-auto mb-2" />
          <div className="text-[17px] font-extrabold text-t0 mb-1">Asset added</div>
          <div className="text-[14px] text-t2 mb-3">ID generated:</div>
          <div className="px-3 py-2 bg-bg2 border border-b0 rounded-lg text-[18px] font-bold font-mono text-p mb-4">{savedId}</div>
          <Bt full onClick={close}>Done</Bt>
        </div>
      </div>;
    }

    const renderField = (f) => {
      const isWide = f.wide || f.type === 'file';
      const cellCls = isWide ? "sm:col-span-2" : "";
      let inner;
      if (f.type === 'subtype') {
        inner = <>
          <div className="text-[14px] text-t2 mb-[3px]">{f.label}{f.required && ' *'}</div>
          <Sl options={subtypes.length ? subtypes.map(s => ({ v: s.cd, l: s.nm })) : [{ v: "", l: `No types defined under ${group?.name || ''} — add one in Settings` }]}
              value={vals.cat || ""} onChange={v => setField('cat', v)} />
        </>;
      } else if (f.type === 'select') {
        inner = <>
          <div className="text-[14px] text-t2 mb-[3px]">{f.label}{f.required && ' *'}</div>
          <Sl options={[{ v: "", l: "Select…" }, ...f.options]} value={vals[f.key] || ""} onChange={v => setField(f.key, v)} />
        </>;
      } else if (f.type === 'file') {
        return <div key={f.key} className={cellCls}>
          <AssetFileField field={f} value={vals[f.key]} onChange={v => setField(f.key, v)} />
        </div>;
      } else {
        inner = <>
          <div className="text-[14px] text-t2 mb-[3px]">{f.label}{f.required && ' *'}</div>
          <Inp type={f.type === 'number' ? 'number' : f.type === 'date' ? 'date' : undefined}
               placeholder={f.placeholder || ""} value={vals[f.key] || ""} onChange={v => setField(f.key, v)} />
        </>;
      }
      return <div key={f.key} className={cellCls}>{inner}</div>;
    };

    return <div className="fixed inset-0 animate-fadeIn bg-black/35 z-[200] flex items-center justify-center p-3" onMouseDown={() => setAstAddModal(false)}>
      <div className="bg-bg1 border border-b1 rounded-2xl p-4 sm:p-6 w-full max-w-[640px] max-h-[90vh] overflow-auto" onMouseDown={e=>e.stopPropagation()}>
        <div className="flex justify-between items-start mb-4">
          <div>
            <div className="text-[18px] font-extrabold text-t0">Add Asset</div>
            {group?.name && <div className="text-[14px] text-t3 mt-[2px]">Adding to <span className="font-semibold text-t1">{group.name}</span> — ID will start with <span className="font-mono font-bold text-p">{group.prefix || 'AS'}</span></div>}
          </div>
          <button onClick={() => setAstAddModal(false)} className="bg-transparent border-0 text-t3 cursor-pointer"><X size={18}/></button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-[14px]">
          <div className="sm:col-span-2">
            <div className="text-[14px] text-t2 mb-[3px]">Asset Category *</div>
            <Sl options={astTypeGroups.map(g => ({ v: String(g.id), l: g.name }))} value={String(groupId || "")} onChange={onPickGroup} />
          </div>

          {fields.map(renderField)}

          <div className="sm:col-span-2">
            <div className="text-[14px] text-t2 mb-[3px]">Centre / Location *</div>
            <Sl options={[{ v: "", l: "Select Centre" }, ...(offices.length ? offices : []).map(o => ({ v: String(o.id), l: (o.nm || o.name) + " — " + (o.city || "") }))]}
                value={office} onChange={setOffice} />
          </div>
          <div className="sm:col-span-2">
            <div className="text-[14px] text-t2 mb-[3px]">Notes (optional)</div>
            <textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="Any other details…"
                      className="w-full min-h-[50px] p-2 bg-bg2 border border-b0 rounded-lg text-t0 text-[14px] outline-none resize-y" />
          </div>
        </div>

        {err && <div className="text-[14px] text-r mb-2 mt-3">{err}</div>}
        <div className="flex gap-[6px] mt-4">
          <Bt full onClick={doSubmit} disabled={saving}>{saving ? "Adding..." : "Add Asset"}</Bt>
          <Bt v="secondary" full onClick={() => setAstAddModal(false)}>Cancel</Bt>
        </div>
      </div>
    </div>;
  };

  const canRequestForOthers=can('assets','requests','request_for_others','edit');
  const myEmpId2=myProfile?.basic?.employee_id||myProfile?.employee_id||null;
  const ReqAstModal=()=>{
    const [af,setAf]=useState({cat:astCats[0]?.cd||"LAPTOP",reason:""});
    const [reqFor,setReqFor]=useState("");
    const [saving,setSaving]=useState(false);const [err,setErr]=useState("");
    const doSubmit=async()=>{
      if(!af.reason.trim())return setErr("Please provide a reason");
      setSaving(true);setErr("");
      const onBehalfOf=(reqFor&&reqFor!==myEmpId2)?reqFor:undefined;
      try{await api.post("/api/assets/request",{category_code:af.cat,reason:af.reason,employee_id:onBehalfOf});notify('Asset request submitted',{title:'Saved',variant:'success'});setAstReqModal(false);refreshAst();}catch(e){setErr(e.message||"Error");notify(e.message||'Failed to submit request',{title:'Error',variant:'error'});}finally{setSaving(false);}
    };
    return <div className="fixed inset-0 animate-fadeIn bg-black/35 z-[200] flex items-center justify-center p-3" onMouseDown={()=>setAstReqModal(false)}><div className="bg-bg1 border border-b1 rounded-2xl p-4 sm:p-6 w-full max-w-[440px] max-h-[85vh] overflow-auto" onMouseDown={e=>e.stopPropagation()}>
      <div className="flex justify-between mb-4"><span className="text-base font-extrabold text-t0">Request Asset</span><button onClick={()=>setAstReqModal(false)} className="bg-transparent border-0 text-t3 cursor-pointer"><X size={18}/></button></div>
      {canRequestForOthers&&<div className="mb-[10px]"><div className="text-[14px] text-t2 mb-[3px]">Request for</div><Sl options={[{v:"",l:"Myself"},...assignableEmployees.filter(e=>e.id!==myEmpId2).map(e=>({v:e.id,l:e.nm+(e.office?" — "+e.office:"")}))]} value={reqFor} onChange={setReqFor}/></div>}
      <div className="mb-[10px]"><div className="text-[14px] text-t2 mb-[3px]">Asset Type</div><Sl options={astCats.map(c=>({v:c.cd,l:c.nm}))} value={af.cat} onChange={v=>setAf({...af,cat:v})}/></div>
      <div className="mb-[10px]"><div className="text-[14px] text-t2 mb-[3px]">Reason *</div><textarea value={af.reason} onChange={e=>setAf({...af,reason:e.target.value})} placeholder="Why do you need this asset?" className="w-full min-h-[60px] p-[10px] bg-bg2 border border-b0 rounded-[10px] text-t0 text-[14px] outline-none resize-y"/></div>
      {err&&<div className="text-[14px] text-r mb-2">{err}</div>}
      <div className="flex gap-[6px]"><Bt full onClick={doSubmit} disabled={saving}>{saving?"Requesting...":"Submit Request"}</Bt><Bt v="secondary" full onClick={()=>setAstReqModal(false)}>Cancel</Bt></div>
    </div></div>;
  };

  const astTabs=["my"];if(canViewInventory)astTabs.push("inventory");if(can('assets','requests',null,'view'))astTabs.push("requests");
  const AstTabBar=()=><div className="inline-flex p-1 bg-bg2 rounded-xl gap-[2px] mb-3 flex-wrap">{astTabs.map(t=>(
    <button
      key={t}
      onClick={()=>setAstTab(t)}
      className={`px-4 py-[7px] rounded-lg text-[13px] font-bold transition-colors flex items-center gap-[6px] cursor-pointer border-0 ${astTab===t ? "bg-s text-t0 shadow-[0_1px_3px_rgba(0,0,0,0.1)]" : "bg-transparent text-t2 hover:text-t0"}`}
    >
      {t==="my"?"My Assets":t==="inventory"?"Inventory":"Requests"}
      {t==="inventory"&&canViewInventory&&<span className="px-[6px] py-[1px] rounded-full text-[10px] font-extrabold" style={{ background: K.pg, color: K.p }}>{effInv.length} Items</span>}
      {t==="requests"&&pendingReqs>0&&<span className="px-[6px] py-[1px] rounded-full text-[10px] font-extrabold" style={{ background: K.ab, color: K.a }}>{pendingReqs} Pending</span>}
    </button>
  ))}</div>;

  const totalAssets=canViewInventory?(astStats?parseInt(astStats.total||0):astInv.length):astMy.length;
  const assignedCount=canViewInventory?(astStats?parseInt(astStats.assigned||0):astInv.filter(a=>a.status==="Assigned").length):astMy.length;
  const availCount=canViewInventory?(astStats?parseInt(astStats.available||0):astInv.filter(a=>a.status==="Available").length):0;
  const repairCount=canViewInventory?(astStats?parseInt(astStats.in_repair||0):astInv.filter(a=>a.status==="In Repair").length):0;
  const pendingReqs=astStats?parseInt(astStats.pending_requests||0):astReqs.filter(r=>r.status==="Pending").length;
  const allocPct=totalAssets>0?Math.round(assignedCount/totalAssets*1000)/10:0;

  const exportInventoryCsv = () => {
    const header = ["Asset Tag","Category","Brand","Model","Serial","Status","Condition","Centre","Assigned To"];
    const rows = effInv.map(a => [a.assetTag||a.id, a.catNm||"", a.brand||"", a.model||"", a.serial||"", a.status||"", a.condition||"", a.office||"", a.assignedTo||""]);
    const csv = [header, ...rows].map(r => r.map(v => `"${String(v).replace(/"/g,'""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url; link.download = `asset-inventory-${new Date().toISOString().slice(0,10)}.csv`; link.click();
    URL.revokeObjectURL(url);
  };

  return <div>
    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 mb-[14px]">
      <div>
        <div className="flex items-center gap-1.5 text-[12px] text-t3 font-medium mb-1">
          <span>Resources</span>
          <ChevronRight size={12} className="text-t3" />
          <span className="text-p font-semibold">Asset Management</span>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <h1 className="m-0 text-2xl font-bold text-t0 tracking-tight">Assets &amp; Inventory</h1>
          <span className="px-[10px] py-[2px] text-[12px] font-semibold rounded-full bg-bg2 text-t2 border border-b0">{totalAssets} Total Registered</span>
        </div>
      </div>
      <div className="flex gap-2 flex-wrap">
        {can('assets','inventory','export_csv','edit')&&<Bt v="secondary" s="sm" icon={Download} onClick={exportInventoryCsv}>Export CSV</Bt>}
        <Bt v="secondary" s="sm" icon={Plus} onClick={()=>setAstReqModal(true)}>Request Asset</Bt>
        {can('assets','inventory','add_asset','edit')&&<Bt icon={Plus} s="sm" onClick={()=>setAstAddModal(true)}>Add Asset</Bt>}
      </div>
    </div>
    <div className={`grid grid-cols-1 sm:grid-cols-2 ${canViewInventory ? "lg:grid-cols-5" : "lg:grid-cols-2"} gap-3 mb-4`}>
      {(canViewInventory ? [
        { icon: Monitor,       label: 'Total Assets', value: totalAssets,   sub: `${astCats.length} categor${astCats.length===1?'y':'ies'} tracked`, c: K.p  },
        { icon: Users,         label: 'Assigned',     value: assignedCount, sub: totalAssets ? `${allocPct}% allocation` : 'No assets yet', c: K.bl },
        { icon: CheckCircle,   label: 'Available',    value: availCount,    sub: 'Ready for deployment', c: K.g  },
        { icon: AlertTriangle, label: 'In Repair',    value: repairCount,   sub: repairCount ? 'Needs attention' : 'None in repair', c: K.a  },
        { icon: Clock,         label: 'Pending Requests', value: pendingReqs, sub: pendingReqs ? 'Awaiting action' : 'All allocations fulfilled', c: K.pu },
      ] : [
        { icon: Monitor, label: 'My Assets',   value: totalAssets,    sub: 'Currently with you', c: K.p },
        { icon: Clock,   label: 'My Requests', value: astReqs.length, sub: 'Total raised', c: K.a },
      ]).map((s,i)=>(
        <div key={i} className="bg-s rounded-2xl border border-b0 p-4 shadow-[0_2px_10px_rgba(15,23,42,0.05)] hover:shadow-[0_6px_18px_rgba(15,23,42,0.08)] transition-shadow flex items-center justify-between gap-2">
          <div className="min-w-0">
            <div className="text-[11px] font-bold text-t3 uppercase tracking-wide truncate">{s.label}</div>
            <div className="text-[22px] font-extrabold text-t0 tracking-tight mt-[2px]">{s.value}</div>
            <div className="text-[11px] text-t3 mt-[2px] truncate">{s.sub}</div>
          </div>
          <div className="w-11 h-11 rounded-2xl flex items-center justify-center flex-shrink-0" style={{ background: `${s.c}14`, color: s.c }}>
            <s.icon size={20} />
          </div>
        </div>
      ))}
    </div>
    <AstTabBar/>
    {astTab==="my"&&<div>
      {astMy.length>0?<div className="grid gap-[10px]" style={{gridTemplateColumns:"repeat(auto-fill,minmax(280px,1fr))"}}>
        {astMy.map(a => {
          const isPendingAck = a.assignState === 'pending_ack';
          const isActive = a.assignState === 'active';
          const isPendingReturn = a.assignState === 'pending_return';
          // Stop click-through on the action buttons so they don't open the
          // detail drawer when the user just wants to acknowledge/return.
          const stop = e => e.stopPropagation();
          return <Cd key={a.id} style={{ borderLeft: `3px solid ${a.catClr || K.p}`, cursor: "pointer" }}
                     onClick={() => { api.get('/api/assets/' + a.id).then(d => setAstDet({ ...a, ...d })).catch(() => setAstDet(a)); }}>
            <div className="flex justify-between items-center mb-[6px] gap-1">
              <Bg c={a.catClr || K.p} sm>{a.catNm}</Bg>
              <AssignStateBadge state={a.assignState} />
            </div>
            <div className="text-[15px] font-bold text-t0 mb-[2px]">{`${a.brand || ''} ${a.model || ''}`.trim() || a.catNm}</div>
            <div className="text-[12px] text-t3 mb-1 font-mono">{a.assetTag ? `Asset ID: ${a.assetTag}` : a.id}{a.serial ? ` · ${a.serial}` : ''}</div>
            <div className="flex justify-between text-[12px] text-t2 mb-[6px]">
              <span>Assigned {a.assignedDate || '—'}{a.assignedByName ? ` by ${a.assignedByName}` : ''}</span>
              {a.expectedReturnDate && <span>Return by {fmtDDMMYY(a.expectedReturnDate)}</span>}
            </div>
            {isPendingAck && (
              <div className="p-2 bg-ab border border-ad rounded-lg mb-2">
                <div className="text-[14px] font-semibold text-a mb-1 flex items-center gap-1"><AlertTriangle size={11} /> Action required</div>
                <div className="text-[14px] text-t2 mb-2">HR has assigned this asset to you. Acknowledge to confirm receipt.</div>
                <div className="flex gap-1" onClick={stop}>
                  <Bt v="success" s="xs" icon={CheckCircle} onClick={() => setAckModalFor(a)}>Acknowledge</Bt>
                  <Bt v="danger" s="xs" icon={X} onClick={() => setRejectModalFor(a)}>Reject</Bt>
                </div>
              </div>
            )}
            {isActive && (
              <div className="flex gap-1 mt-1" onClick={stop}>
                <Bt v="ghost" s="xs" icon={FileText}
                    onClick={async () => {
                      try {
                        const r = await fetch(`${BASE}/api/assets/handover-doc/${a.assignmentId}`, { headers: { Authorization: `Bearer ${api.getToken()}` } });
                        if (!r.ok) throw new Error('Handover doc not available');
                        const blob = await r.blob();
                        window.open(URL.createObjectURL(blob), '_blank');
                      } catch (e) { notify(e.message || 'Failed to open handover doc', { title: 'Error', variant: 'error' }); }
                    }}>
                  Handover Doc
                </Bt>
                <Bt v="secondary" s="xs" icon={RefreshCw} disabled={returningAstId === a.id}
                    onClick={async () => {
                      if (!(await confirmDialog('Initiate return for this asset? HR will inspect and complete it.'))) return;
                      setReturningAstId(a.id);
                      try {
                        await api.post(`/api/assets/${a.id}/initiate-return`);
                        notify('Return initiated', { title: 'Saved', variant: 'success' });
                        refreshAst();
                      }
                      catch (e) { notify(e.message || 'Failed to initiate return', { title: 'Error', variant: 'error' }); }
                      finally { setReturningAstId(null); }
                    }}>{returningAstId === a.id ? 'Returning…' : 'Return'}</Bt>
              </div>
            )}
            {isPendingReturn && (
              <div className="text-[14px] text-bl p-2 bg-bg2 border border-b0 rounded-lg">
                Return inspection pending with HR.
              </div>
            )}
          </Cd>;
        })}
      </div>:<div className="text-center p-[30px] text-t3 text-[14px]">No assets assigned to you</div>}
    </div>}
    {astTab==="inventory"&&canViewInventory&&(()=>{
      const filtered = filteredInv;
      // Per-row identifier: brand+model when present, else fall back to the
      // category-specific attribute that means "what is this thing" (vehicle
      // number, card number, sim number, asset name for Others).
      const itemLabel = (r) => {
        const bm = `${r.brand || ''} ${r.model || ''}`.trim();
        if (bm) return bm;
        const at = r.attributes || {};
        return at.asset_name || at.vehicle_no || at.card_no || at.sim_no || '—';
      };
      // One-click status chips — same pattern as the Requests tab, so
      // "show me everyone's Assigned assets" is a single click instead of
      // opening the status dropdown.
      const invStatusChips = [
        { v: "", l: "All" },
        { v: "Assigned", l: "Assigned" },
        { v: "Available", l: "Available" },
        { v: "In Repair", l: "In Repair" },
      ];
      // Distinct centres actually present in the current dataset — avoids
      // offering a filter option with zero matching rows.
      const invOffices = [...new Set(effInv.map(a => a.office).filter(Boolean))].sort();
      const disposedCount = disposedInv ? disposedInv.filter(a => a.status === 'Disposed').length : null;

      // Bottom health-deck figures — all derived from effInv, no invented
      // metrics (no fabricated "AMC" contracts or hiring-pipeline claims).
      const withWarranty = astInv.filter(a => a.warranty);
      const today = new Date();
      const in60 = new Date(today.getTime() + 60 * 86400000);
      const expiringSoon = withWarranty.filter(a => { const d = new Date(a.warranty); return d >= today && d <= in60; });
      const officeCounts = {};
      astInv.forEach(a => { if (a.office) officeCounts[a.office] = (officeCounts[a.office] || 0) + 1; });
      const officeEntries = Object.entries(officeCounts).sort((a, b) => b[1] - a[1]);

      return <div>
        <Cd style={{ padding: 12 }}>
          <div className="flex items-center gap-[6px] mb-3 flex-wrap">
            {invStatusChips.map(f=>{
              const on = invStFilter === f.v;
              const cnt = f.v ? effInv.filter(a=>a.status===f.v).length : effInv.length;
              return (
                <button
                  key={f.v||"all"}
                  onClick={()=>setEditVals(prev=>({...prev,_invStFilter:f.v}))}
                  className={`px-3 py-1 rounded-lg border text-[14px] font-semibold cursor-pointer flex items-center gap-1 ${on ? "border-p bg-pg text-p" : "border-b0 bg-transparent text-t3"}`}
                >{f.l}<span className="text-[12px] opacity-70">({cnt})</span></button>
              );
            })}
          </div>
          {/* Single-line toolbar on desktop — search grows to fill, filters keep
              fixed widths. Wraps below `lg` so the fixed-width filters don't get
              squeezed off-screen at tablet/split-screen widths (700–1024px),
              where the sidebar can still take ~220px. */}
          <div className="flex gap-2 items-center flex-wrap lg:flex-nowrap">
            <div className="flex-1 min-w-0">
              <Inp icon={Search} placeholder="Search brand, model, serial, ID..." value={invSearch} onChange={v=>setEditVals(prev=>({...prev,_invSearch:v}))} />
            </div>
            <div className="w-[150px] flex-shrink-0">
              <Sl options={[{v:"",l:"All Categories"},...astCats.map(c=>({v:c.cd,l:c.nm}))]} value={invCatFilter} onChange={v=>setEditVals(prev=>({...prev,_invCatFilter:v}))}/>
            </div>
            {invOffices.length > 0 && <div className="w-[150px] flex-shrink-0">
              <Sl options={[{v:"",l:"All Centres"},...invOffices.map(o=>({v:o,l:o}))]} value={invOfcFilter} onChange={v=>setEditVals(prev=>({...prev,_invOfcFilter:v}))}/>
            </div>}
            <label className="flex items-center gap-[4px] text-[14px] text-t2 whitespace-nowrap cursor-pointer flex-shrink-0">
              <input type="checkbox" checked={showDisposed} onChange={e => setShowDisposed(e.target.checked)} /> Show disposed{disposedCount !== null ? ` (${disposedCount})` : ''}
            </label>
            <button onClick={refreshAst} title="Refresh" className="p-[7px] text-t3 hover:text-t1 hover:bg-bg2 rounded-lg transition-colors flex-shrink-0"><RefreshCw size={14} /></button>
          </div>
        </Cd>
        <div className="rounded-2xl border border-b0 overflow-hidden shadow-card mt-3">
          <Tbl bare cols={[
            {k:"tag",l:"Asset Tag",r:r=><span className="font-mono text-[12px] font-bold text-t0">{r.assetTag||r.id}</span>},
            {k:"cat",l:"Asset Type",r:r=><Bg c={r.catClr||K.p} sm>{r.catNm}</Bg>},
            {k:"item",l:"Item",r:r=><span className="text-[14px] font-semibold cursor-pointer text-pl" onClick={()=>{api.get('/api/assets/'+r.id).then(d=>setAstDet({...r,...d})).catch(console.error);}}>{itemLabel(r)}</span>},
            {k:"st",l:"Status",r:r=><Bg c={stClrA(r.status)} sm dot>{r.status}</Bg>},
            {k:"cond",l:"Condition",r:r=><Bg c={condClr(r.condition)} sm>{r.condition}</Bg>},
            {k:"office",l:"Centre",r:r=><span className="text-[14px] text-t2">{r.office||"—"}</span>},
            {k:"to",l:"Assigned To",r:r=>r.assignedTo?<span className="text-[14px]">{r.assignedTo}</span>:<span className="text-[12px] text-t3">—</span>},
            {k:"v",l:"",r:r=><Bt v="ghost" s="xs" icon={Eye} onClick={()=>{api.get('/api/assets/'+r.id).then(d=>setAstDet({...r,...d})).catch(console.error);}}>View</Bt>}
          ]} data={invPage.pageData}/>
          <Pagination total={filtered.length} page={invPage.page} pageSize={invPage.pageSize} onPageChange={invPage.onPageChange} onPageSizeChange={invPage.onPageSizeChange} />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-4">
          <div className="bg-bg1 border border-b0 rounded-2xl p-4 shadow-sm flex items-center justify-between gap-3">
            <div className="min-w-0">
              <div className="text-[11px] font-bold text-t3 uppercase tracking-wide">Warranty Coverage</div>
              <h4 className="text-[15px] font-bold text-t0 mt-1">{withWarranty.length} of {astInv.length} on file</h4>
              <p className={`text-[12px] font-medium mt-1 flex items-center gap-1 ${expiringSoon.length ? "text-a" : "text-g"}`}>
                <ShieldCheck size={12} />
                {expiringSoon.length ? `${expiringSoon.length} expiring within 60 days` : withWarranty.length ? "None expiring within 60 days" : "No warranty dates recorded yet"}
              </p>
            </div>
            <div className="w-12 h-12 rounded-xl flex items-center justify-center font-bold text-[13px] flex-shrink-0" style={{ background: K.gb, color: K.g }}>{withWarranty.length}/{astInv.length || 0}</div>
          </div>
          <div className="bg-bg1 border border-b0 rounded-2xl p-4 shadow-sm flex items-center justify-between gap-3">
            <div className="min-w-0">
              <div className="text-[11px] font-bold text-t3 uppercase tracking-wide">Deployment Buffer</div>
              <h4 className="text-[15px] font-bold text-t0 mt-1">{availCount} Ready in Stock</h4>
              <p className="text-[12px] text-bl font-medium mt-1 flex items-center gap-1"><PackageCheck size={12} /> Available for immediate assignment</p>
            </div>
            <div className="w-12 h-12 rounded-xl flex items-center justify-center font-bold text-[13px] flex-shrink-0" style={{ background: K.pg, color: K.p }}>{totalAssets ? `${Math.round(availCount/totalAssets*100)}%` : "—"}</div>
          </div>
          <div className="bg-bg1 border border-b0 rounded-2xl p-4 shadow-sm flex items-center justify-between gap-3">
            <div className="min-w-0">
              <div className="text-[11px] font-bold text-t3 uppercase tracking-wide">Facility Distribution</div>
              <h4 className="text-[15px] font-bold text-t0 mt-1">{officeEntries.length} Centre{officeEntries.length===1?'':'s'}</h4>
              <p className="text-[12px] text-t3 font-medium mt-1 flex items-center gap-1 truncate" title={officeEntries.map(([o,c])=>`${o} (${c})`).join(' · ')}>
                <Building2 size={12} className="flex-shrink-0" />
                {officeEntries.length ? officeEntries.map(([o,c])=>`${o} (${c})`).join(' · ') : 'No centre data recorded'}
              </p>
            </div>
            <div className="w-12 h-12 rounded-xl flex items-center justify-center font-bold text-[13px] flex-shrink-0" style={{ background: K.pub, color: K.pu }}>{officeEntries.length}</div>
          </div>
        </div>
      </div>;
    })()}
    {astTab==="requests"&&<div>
      <div className="flex gap-[6px] mb-[10px] flex-wrap">
        {["All","Pending","Manager Approved","Awaiting Inventory","Approved","Rejected","Cancelled"].map(f=>{
          const on=(editVals._astFilter||null)===(f==="All"?null:f);
          const cnt = f === "All" ? astReqs.length : astReqs.filter(r => r.status === f).length;
          return (
            <button
              key={f}
              onClick={()=>setEditVals(prev=>({...prev,_astFilter:f==="All"?null:f}))}
              className={`px-3 py-1 rounded-lg border text-[14px] font-semibold cursor-pointer flex items-center gap-1 ${on ? "border-p bg-pg text-p" : "border-b0 bg-transparent text-t3"}`}
            >{f}<span className="text-[12px] opacity-70">({cnt})</span></button>
          );
        })}
      </div>
      <Tbl cols={[
        {k:"id",l:"ID",r:r=><span className="font-mono text-[12px] font-bold text-t1">{r.id}</span>},
        ...(role!=="employee"?[{k:"emp",l:"Employee",r:r=><span className="text-[15px] font-bold text-t0">{r.enm}</span>}]:[]),
        {k:"cat",l:"Type",r:r=><Bg c={r.catClr||K.p} sm>{r.catNm}</Bg>},
        {k:"reason",l:"Reason / Outcome",r:r=>{
          if(r.status==='Rejected'&&r.rejectionReason) return <span className="text-[12px] text-r truncate max-w-[160px] inline-block" title={r.rejectionReason}>✕ {r.rejectionReason}</span>;
          if(r.status==='Approved'&&r.fulfilledLabel) return <span className="text-[12px] text-g truncate max-w-[200px] inline-block" title={r.fulfilledLabel}>✓ {r.fulfilledLabel} ({r.fulfilledId}){r.handoverPhotoPath && <a href={uploadUrl(r.handoverPhotoPath)} target="_blank" rel="noreferrer" className="ml-1 underline text-bl" onClick={e=>e.stopPropagation()}>Photo</a>}</span>;
          if(r.status==='Awaiting Inventory') return <span className="text-[12px] text-bl truncate max-w-[160px] inline-block">Awaiting stock · {r.reason}</span>;
          if(r.status==='Manager Approved') return <span className="text-[12px] text-pu truncate max-w-[160px] inline-block" title={r.reason}>✓ Approved by {r.managerName||'manager'} · awaiting HR</span>;
          if(r.status==='Pending') return <span className="text-[12px] text-t2 truncate max-w-[160px] inline-block" title={r.reason}>Awaiting HR approval</span>;
          return <span className="text-[12px] text-t2 max-w-[160px] overflow-hidden text-ellipsis whitespace-nowrap inline-block" title={r.reason}>{r.reason}</span>;
        }},
        {k:"dt",l:"Requested",r:r=><span className="text-[12px] text-t3">{r.createdAt?fmtDMY(r.createdAt):"—"}</span>},
        {k:"st",l:"Status",r:r=><Bg c={r.status==="Approved"?K.g:r.status==="Rejected"?K.r:r.status==="Awaiting Inventory"?K.bl:r.status==="Manager Approved"?K.pu:r.status==="Cancelled"?K.t3:K.a} dot sm>{r.status}</Bg>},
        {k:"act",l:"",r:r=>{
          // HR approves every request directly — the reporting manager is
          // not part of this workflow at all (no manager-only action here).
          const canApprove = can('assets','requests','approve_request','approve');
          const canReject = can('assets','requests','reject_request','reject');
          // The requests list itself is already centre-scoped server-side
          // for centre_hr, so every row reaching this render is already
          // within their assigned centre — no extra per-row check needed.
          // 'Manager Approved' stays accepted here too, purely so any
          // request already sitting in that state from before this change
          // can still be finished normally.
          const actionable = ['Pending','Manager Approved','Awaiting Inventory'].includes(r.status);
          if (actionable && (canApprove || canReject)) return <div className="flex gap-1">
            {canApprove && <Bt v="success" s="xs" icon={CheckCircle} onClick={() => setApproveReqFor(r)}>{r.status === 'Awaiting Inventory' ? 'Fulfil' : 'Approve'}</Bt>}
            {canReject && <Bt v="danger" s="xs" icon={X} onClick={() => setRejectReqFor(r.id)}>Reject</Bt>}
          </div>;
          // A fulfilled request has a real asset behind it — fetch it so
          // the detail modal opens on the actual asset (with working
          // Manage Asset actions) instead of the bare request row, which
          // has no `assets` table row behind its own id and would 404 any
          // save/scrap/delete action against it.
          return <Bt v="ghost" s="xs" icon={Eye} onClick={() => {
            if (r.fulfilledId) api.get('/api/assets/' + r.fulfilledId).then(d => setAstDet({ ...r, ...d })).catch(() => setAstDet(r));
            else setAstDet(r);
          }}>View</Bt>;
        }}
      ]} data={editVals._astFilter?astReqs.filter(r=>r.status===editVals._astFilter):astReqs}/>
      {astReqs.length===0&&<div className="text-center p-[30px] text-t3 text-[14px]">No asset requests</div>}
    </div>}
    {astDet&&<div className="fixed inset-0 animate-fadeIn bg-black/60 z-[200] flex items-center justify-center p-3" onMouseDown={()=>setAstDet(null)}>
      <div className="bg-bg1 rounded-2xl w-full max-w-[580px] max-h-[90vh] overflow-auto shadow-[0_24px_80px_rgba(0,0,0,0.25)]" onMouseDown={e=>e.stopPropagation()}>
        {(()=>{
          // A request object (id always "AR######") never has a real asset
          // fetched onto it unless the "View" handler resolved a fulfilled
          // asset — checking the id prefix alone used to break once newer
          // inventory ids stopped starting with "AST" (they're now a
          // 2-letter group prefix like "EL00001").
          const isInventory=!!astDet.asset||!(astDet.id||"").startsWith("AR");
          const a=astDet.asset||astDet;
          const assignments=astDet.assignments||[];
          return <div>
            {/* Header band — only the title block + close button live in the
                flex row. Everything else is a vertical stack of sections in
                the body below. */}
            <div className="px-6 py-[18px] border-b border-b0 rounded-t-2xl" style={{ background: `linear-gradient(135deg,${stClrA(a.status)}15,${K.bg2})` }}>
              <div className="flex justify-between items-start gap-3">
                <div className="flex-1 min-w-0">
                  <div className="text-[18px] font-extrabold text-t0 truncate">
                    {isInventory ? (`${a.brand || ''} ${a.model || ''}`.trim() || a.catNm || 'Asset') : "Asset Request"}
                  </div>
                  <div className="text-[14px] text-t3 font-mono mt-[2px]">{a.id}{(a.asset_tag || a.assetTag) ? ` · Asset ID: ${a.asset_tag || a.assetTag}` : ''}</div>
                  <div className="flex gap-2 mt-[6px] items-center">
                    {a.catNm && <Bg c={a.catClr || K.p} sm>{a.catNm}</Bg>}
                    {a.status && <Bg c={stClrA(a.status)} sm dot>{a.status}</Bg>}
                  </div>
                </div>
                <button onClick={() => setAstDet(null)} className="bg-transparent border-0 text-t3 cursor-pointer p-1"><X size={18} /></button>
              </div>
            </div>

            {/* Body — vertical sections, padded uniformly. */}
            <div className="px-6 py-[14px] flex flex-col gap-[14px]">

              {a.attributes && Object.keys(a.attributes).length > 0 && (
                <div>
                  <div className="text-[14px] font-bold text-t0 mb-2">Details</div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {Object.entries(a.attributes).map(([k, v]) => {
                      if (v === null || v === undefined || v === "") return null;
                      const label = k.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
                      if (typeof v === 'object' && v.path) {
                        return <div key={k} className="bg-bg2 rounded-lg p-2 border border-b0">
                          <div className="text-[12px] text-t3 uppercase">{label}</div>
                          <a href={`${BASE}${v.path}`} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-[14px] text-p font-semibold no-underline mt-[2px]">
                            <FileText size={11} />{v.name || 'Open'}
                          </a>
                        </div>;
                      }
                      return <div key={k} className="bg-bg2 rounded-lg p-2 border border-b0">
                        <div className="text-[12px] text-t3 uppercase">{label}</div>
                        <div className="text-[14px] font-semibold text-t0 mt-[1px]">{String(v)}</div>
                      </div>;
                    })}
                  </div>
                </div>
              )}

              {astDet.currentAssignment && (
                <div className="border-t border-b0 pt-[14px]">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-[14px] font-bold text-t0">Current Assignment</div>
                    <AssignStateBadge state={astDet.currentAssignment.state} />
                  </div>
                  <div className="bg-bg2 border border-b0 rounded-lg p-3 text-[14px]">
                    <div className="flex items-center gap-2 mb-1">
                      <Av nm={astDet.currentAssignment.employee_name || ''} sz={20} />
                      <div className="flex-1">
                        <div className="font-semibold text-t0">{astDet.currentAssignment.employee_name || astDet.currentAssignment.employee_id}</div>
                        <div className="text-[12px] text-t3 font-mono">{astDet.currentAssignment.employee_id}</div>
                      </div>
                    </div>
                    <div className="text-[14px] text-t2">Assigned {fmtDDMMYY(astDet.currentAssignment.assigned_date)}{astDet.currentAssignment.assigned_condition ? ` · ${astDet.currentAssignment.assigned_condition} condition` : ''}</div>
                    {astDet.currentAssignment.expected_return_date && <div className="text-[14px] text-t2">Expected return: {fmtDDMMYY(astDet.currentAssignment.expected_return_date)}</div>}
                    {astDet.currentAssignment.assignment_note && <div className="text-[14px] text-t2 mt-1 italic">"{astDet.currentAssignment.assignment_note}"</div>}
                    {astDet.currentAssignment.acknowledged_at && <div className="text-[14px] text-g mt-1">✓ Acknowledged {fmtDMY(astDet.currentAssignment.acknowledged_at)}</div>}
                  </div>
                  {(can('assets','requests','cancel_assignment','edit') || can('assets','requests','initiate_return','edit') || can('assets','requests','complete_return','edit')) && (
                    <div className="flex gap-2 mt-2 flex-wrap">
                      <Bt v="ghost" s="xs" icon={FileText}
                          onClick={async () => {
                            try {
                              const r = await fetch(`${BASE}/api/assets/handover-doc/${astDet.currentAssignment.id}`, { headers: { Authorization: `Bearer ${api.getToken()}` } });
                              if (!r.ok) throw new Error('Doc not available');
                              window.open(URL.createObjectURL(await r.blob()), '_blank');
                            } catch (e) { notify(e.message || 'Failed to open handover doc', { title: 'Error', variant: 'error' }); }
                          }}>Handover Doc</Bt>
                      {astDet.currentAssignment.state === 'pending_ack' && can('assets','requests','cancel_assignment','edit') && (
                        <Bt v="danger" s="xs" icon={X} disabled={astDetBusy === 'cancel'}
                            onClick={async () => {
                              if (!(await confirmDialog('Cancel this assignment? Asset returns to the available pool.'))) return;
                              setAstDetBusy('cancel');
                              try {
                                await api.post(`/api/assets/${a.id}/cancel-assignment`);
                                notify('Assignment cancelled', { title: 'Saved', variant: 'success' });
                                setAstDet(null); refreshAst();
                              }
                              catch (e) { notify(e.message || 'Failed to cancel assignment', { title: 'Error', variant: 'error' }); }
                              finally { setAstDetBusy(''); }
                            }}>{astDetBusy === 'cancel' ? 'Cancelling…' : 'Cancel Assignment'}</Bt>
                      )}
                      {astDet.currentAssignment.state === 'active' && can('assets','requests','initiate_return','edit') && (
                        <Bt v="secondary" s="xs" icon={RefreshCw} disabled={astDetBusy === 'return'}
                            onClick={async () => {
                              if (!(await confirmDialog('Initiate return? Inspection will then be due.'))) return;
                              setAstDetBusy('return');
                              try {
                                await api.post(`/api/assets/${a.id}/initiate-return`);
                                notify('Return initiated', { title: 'Saved', variant: 'success' });
                                const d = await api.get('/api/assets/' + a.id); setAstDet({ ...astDet, ...d }); refreshAst();
                              }
                              catch (e) { notify(e.message || 'Failed to initiate return', { title: 'Error', variant: 'error' }); }
                              finally { setAstDetBusy(''); }
                            }}>{astDetBusy === 'return' ? 'Initiating…' : 'Initiate Return'}</Bt>
                      )}
                      {astDet.currentAssignment.state === 'pending_return' && can('assets','requests','complete_return','edit') && (
                        <Bt v="success" s="xs" icon={CheckCircle}
                            onClick={() => setCompleteReturnFor({ ...a, currentEmployee: astDet.currentAssignment.employee_name })}>
                          Complete Return Inspection
                        </Bt>
                      )}
                    </div>
                  )}
                </div>
              )}

              {assignments.length > 0 && (
                <div className="border-t border-b0 pt-[14px]">
                  <div className="text-[14px] font-bold text-t0 mb-2">Assignment History</div>
                  {assignments.map((h, i) => (
                    <div key={i} className="flex items-center gap-2 py-[6px]" style={{ borderBottom: `1px solid ${K.b0}30` }}>
                      <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: h.is_active ? K.g : K.t3 }} />
                      <div className="flex-1">
                        <div className="text-[14px] font-semibold text-t0">{h.employee_name || "Unknown"}</div>
                        <div className="text-[12px] text-t3">{h.assigned_date ? fmtDMY(h.assigned_date) : ""} → {h.is_active ? "Current" : (h.returned_date ? fmtDMY(h.returned_date) : "—")}{h.returned_condition ? " · Returned: " + h.returned_condition : ""}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {can('assets','inventory','view_activity_log','view') && a.id && (
                <div className="border-t border-b0 pt-[14px]">
                  <div className="flex items-center gap-1 text-[14px] font-bold text-t0 mb-[10px]"><History size={13} color={K.p} /> Activity Log</div>
                  <AssetActivityLog assetId={a.id} />
                </div>
              )}

              {/* Disposal banner — shown when the asset is written off. Sits
                  above Manage Asset so it's the first thing HR sees on a
                  disposed item. Pulls all the disposal metadata from
                  astDet.asset.disposal (set by GET /api/assets/:id). */}
              {a.status === 'Disposed' && a.disposal && (
                <div className="border-t border-b0 pt-[14px]">
                  <div className="p-3 rounded-lg bg-rb border border-rd">
                    <div className="flex items-center gap-2 mb-2">
                      <AlertTriangle size={14} color={K.r} />
                      <div className="text-[14px] font-bold text-r">Disposed · {a.disposal.method}</div>
                    </div>
                    <div className="text-[14px] text-t0 mb-1">{a.disposal.reason}</div>
                    <div className="text-[14px] text-t2 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-[2px]">
                      <div>By: <span className="font-semibold">{a.disposal.disposed_by || '—'}</span></div>
                      <div>On: <span className="font-semibold">{a.disposal.disposed_at ? `${fmtDMY(a.disposal.disposed_at)}, ${new Date(a.disposal.disposed_at).toLocaleTimeString('en-IN')}` : '—'}</span></div>
                      {a.disposal.value != null && <div>Recovery: <span className="font-semibold">₹ {a.disposal.value}</span></div>}
                      {a.disposal.proof_path && <div><a href={`${BASE}${a.disposal.proof_path}`} target="_blank" rel="noreferrer" className="text-p font-semibold no-underline">📎 Proof attached</a></div>}
                    </div>
                    {can('assets','inventory','restore_asset','edit') && <div className="mt-2">
                      <Bt v="secondary" s="xs" icon={RefreshCw} disabled={astDetBusy === 'restore'}
                          onClick={async () => {
                            if (!(await confirmDialog(`Restore ${a.id}? It will return to Available status. The original disposal stays in the audit log.`))) return;
                            setAstDetBusy('restore');
                            try {
                              await api.post(`/api/assets/${a.id}/restore`);
                              notify('Asset restored', { title: 'Saved', variant: 'success' });
                              const d = await api.get('/api/assets/' + a.id);
                              setAstDet({ ...astDet, ...d });
                              refreshAst();
                            } catch (e) { notify(e.message || 'Failed to restore asset', { title: 'Error', variant: 'error' }); }
                            finally { setAstDetBusy(''); }
                          }}>{astDetBusy === 'restore' ? 'Restoring…' : 'Restore Asset'}</Bt>
                    </div>}
                  </div>
                </div>
              )}

              {isInventory && can('assets','inventory',null,'edit') && a.status !== 'Disposed' && (
                <div className="border-t border-b0 pt-[14px]">
                  <div className="text-[14px] font-bold text-t0 mb-[10px]">Manage Asset</div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-[10px]">
                    <div><div className="text-[12px] text-t3 mb-[3px]">Status</div><Sl options={[{v:"Available",l:"Available"},{v:"Assigned",l:"Assigned"},{v:"In Repair",l:"In Repair"}]} value={editVals._astStatus||a.status} onChange={v=>setEditVals(prev=>({...prev,_astStatus:v}))}/></div>
                    <div><div className="text-[12px] text-t3 mb-[3px]">Condition</div><Sl options={[{v:"New",l:"New"},{v:"Good",l:"Good"},{v:"Fair",l:"Fair"},{v:"Damaged",l:"Damaged"}]} value={editVals._astCond||a.condition} onChange={v=>setEditVals(prev=>({...prev,_astCond:v}))}/></div>
                    <div><div className="text-[12px] text-t3 mb-[3px]">Centre / Location</div><Sl options={[{v:"",l:"Select"},...(offices.length?offices:[]).map(o=>({v:String(o.id),l:(o.nm||o.name)+" — "+(o.city||"")}))]} value={editVals._astOffice||String(a.office_id||"")} onChange={v=>setEditVals(prev=>({...prev,_astOffice:v}))}/></div>
                  </div>
                  <div className="mb-[10px]"><div className="text-[12px] text-t3 mb-[3px]">Notes</div><textarea value={editVals._astNotes!==undefined?editVals._astNotes:a.notes||""} onChange={e=>setEditVals(prev=>({...prev,_astNotes:e.target.value}))} placeholder="Maintenance notes, repair details..." className="w-full min-h-[50px] p-2 bg-bg2 border border-b0 rounded-lg text-t0 text-[14px] outline-none resize-y box-border"/></div>
                  <div className="flex gap-[6px] flex-wrap">
                    <Bt icon={CheckCircle} disabled={astDetBusy === 'save'} onClick={async()=>{setAstDetBusy('save');try{await api.put('/api/assets/'+a.id,{status:editVals._astStatus||undefined,condition:editVals._astCond||undefined,notes:editVals._astNotes!==undefined?editVals._astNotes:undefined,office_id:editVals._astOffice||undefined});notify('Asset details updated',{title:'Saved',variant:'success'});setAstDet(null);setEditVals(prev=>({...prev,_astStatus:"",_astCond:"",_astNotes:"",_astOffice:""}));refreshAst();}catch(e){notify(e.message||'Failed to save changes',{title:'Error',variant:'error'});}finally{setAstDetBusy('');}}}>{astDetBusy==='save'?'Saving…':'Save Changes'}</Bt>
                    {a.status==="Available" && !astDet.currentAssignment && <Bt v="success" icon={UserCheck} onClick={() => setAssignModalFor(a)}>Assign Asset</Bt>}
                    {/* Scrap is hidden when the asset is currently held by an
                        employee — the proper path there is Initiate Return →
                        Complete Return Inspection (with the Dispose option). */}
                    {!astDet.currentAssignment && <Bt v="danger" s="sm" icon={AlertTriangle} onClick={() => setScrapModalFor(a)}>Scrap / Write Off</Bt>}
                  </div>
                </div>
              )}

              {/* Centre HR: assign only, scoped to their centre server-side —
                  no edit/status/scrap access (that stays admin/hr_head above). */}
              {isInventory && !can('assets','inventory',null,'edit') && can('assets','inventory','assign_asset','edit') && a.status === 'Available' && !astDet.currentAssignment && (
                <div className="border-t border-b0 pt-[14px]">
                  <Bt v="success" icon={UserCheck} onClick={() => setAssignModalFor(a)}>Assign Asset</Bt>
                </div>
              )}

              {/* Danger Zone — permanent delete. Admin only. Use for
                  duplicates, typos at creation, pilot data. The normal
                  end-of-life is Scrap / Write Off (above), which keeps
                  the row + audit trail. Purge disappears everything. */}
              {isInventory && can('assets','inventory','purge_asset','edit') && !astDet.currentAssignment && (
                <div className="border-t border-b0 pt-[14px] mt-[10px]">
                  <div className="p-3 rounded-lg" style={{ borderLeft: `3px solid ${K.r}`, background: K.bg2 }}>
                    <div className="flex items-center gap-2 mb-[6px]">
                      <AlertTriangle size={13} color={K.r} />
                      <span className="text-[14px] font-bold text-r">Danger Zone</span>
                    </div>
                    <div className="text-[14px] text-t2 mb-[10px]">
                      <b className="text-t0">Permanently delete</b> the asset row and its entire history (assignments, audit log, request references). Writes a JSON backup to <code className="bg-bg1 px-1 rounded">server/backups/purged-assets/</code> before the delete runs. Use for duplicate rows / typos at creation only — the correct path for a genuine end-of-life is Scrap / Write Off above.
                    </div>
                    <Bt v="danger" s="sm" icon={AlertTriangle} onClick={() => { setPurgeAsset(a); setPurgeConfirm(''); }}>
                      Permanently Delete Asset
                    </Bt>
                  </div>
                </div>
              )}
              {isInventory && can('assets','inventory','purge_asset','edit') && astDet.currentAssignment && (
                <div className="border-t border-b0 pt-[14px] mt-[10px]">
                  <div className="p-3 rounded-lg text-[14px] text-t3" style={{ borderLeft: `3px solid ${K.b1}`, background: K.bg2 }}>
                    Permanent delete is unavailable while the asset is assigned. Cancel the assignment or initiate return first.
                  </div>
                </div>
              )}
            </div>
          </div>;
        })()}
      </div>
    </div>}
    {astAddModal&&<AddAstModal/>}
    {astReqModal&&<ReqAstModal/>}

    {assignModalFor && <AssignAssetModal
      assetId={assignModalFor.id} asset={assignModalFor} employees={assignableEmployees}
      onClose={() => setAssignModalFor(null)}
      onSaved={async () => { setAstDet(null); refreshAst(); }} />}

    {ackModalFor && <AcknowledgeModal
      assetId={ackModalFor.id} asset={ackModalFor}
      employeeName={myProfile?.name || myProfile?.nm || ''}
      onClose={() => setAckModalFor(null)}
      onSaved={() => refreshAst()} />}

    {rejectModalFor && <RejectAssignmentModal
      assetId={rejectModalFor.id}
      onClose={() => setRejectModalFor(null)}
      onSaved={() => refreshAst()} />}

    {completeReturnFor && <CompleteReturnModal
      assetId={completeReturnFor.id}
      currentEmployee={completeReturnFor.currentEmployee}
      onClose={() => setCompleteReturnFor(null)}
      onSaved={async () => { setAstDet(null); refreshAst(); }} />}

    {scrapModalFor && <ScrapAssetModal
      assetId={scrapModalFor.id} asset={scrapModalFor}
      onClose={() => setScrapModalFor(null)}
      onSaved={async () => { setAstDet(null); refreshAst(); if (showDisposed) { const d = await api.get('/api/assets/inventory?include_disposed=1'); setDisposedInv(Array.isArray(d) ? d : (d?.data || [])); } }} />}

    {approveReqFor && <ApproveRequestModal
      request={approveReqFor}
      onClose={() => setApproveReqFor(null)}
      onSaved={() => refreshAst()} />}

    {rejectReqFor && <RejectRequestModal
      requestId={rejectReqFor}
      onClose={() => setRejectReqFor(null)}
      onSaved={() => refreshAst()} />}

    {/* Permanent asset delete confirmation. Requires the admin to type
        the asset tag verbatim — same protection pattern as Employees
        purge. Modal cannot be dismissed while the request is in-flight. */}
    {purgeAsset && (
      <div className="fixed inset-0 animate-fadeIn bg-black/50 z-[300] flex items-center justify-center p-3" onMouseDown={() => !purgeBusy && setPurgeAsset(null)}>
        <div className="animate-modalIn bg-bg1 border rounded-2xl p-5 w-full max-w-[768px] shadow-[0_20px_60px_rgba(200,0,0,0.25)]"
             style={{ borderColor: `${K.r}55` }}
             onMouseDown={e => e.stopPropagation()}>
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle size={18} color={K.r} />
            <span className="text-[17px] font-extrabold text-r">Permanently delete asset?</span>
          </div>
          <div className="text-[14px] text-t1 leading-[1.6] mb-3">
            This will <b className="text-r">permanently delete</b> asset <span className="font-mono font-bold text-t0">{purgeAsset.id}</span>
            {(purgeAsset.brand || purgeAsset.model) ? <> ({[purgeAsset.brand, purgeAsset.model].filter(Boolean).join(' ')})</> : null}
            , its assignment history, its audit log, and its request references. A JSON backup of everything is written to <code className="bg-bg2 px-1 rounded text-[14px]">server/backups/purged-assets/</code> before the delete runs.
          </div>
          <div className="text-[14px] text-t2 mb-1">
            Type <b className="text-t0 font-mono">{purgeAsset.id}</b> to confirm:
          </div>
          <input
            type="text"
            value={purgeConfirm}
            onChange={e => setPurgeConfirm(e.target.value)}
            placeholder={purgeAsset.id}
            className="w-full bg-bg2 border border-b1 rounded-lg px-3 py-2 text-[15px] font-mono text-t0 focus:outline-none focus:border-r mb-3"
            autoFocus
          />
          <div className="flex gap-2">
            <Bt v="secondary" full onClick={() => setPurgeAsset(null)} disabled={purgeBusy}>Cancel</Bt>
            <Bt v="danger" full disabled={purgeBusy || purgeConfirm !== purgeAsset.id}
                onClick={async () => {
                  setPurgeBusy(true);
                  try {
                    const r = await api.del('/api/assets/' + purgeAsset.id + '/purge', { confirmation: purgeAsset.id });
                    notify(`Asset ${purgeAsset.id} purged. Backup: ${r.backup}`, { title: 'Saved', variant: 'success' });
                    setPurgeAsset(null);
                    setAstDet(null);
                    refreshAst();
                  } catch (e) {
                    notify(e.message || 'Purge failed', { title: 'Error', variant: 'error' });
                  } finally {
                    setPurgeBusy(false);
                  }
                }}>
              {purgeBusy ? 'Purging…' : 'Purge permanently'}
            </Bt>
          </div>
        </div>
      </div>
    )}
  </div>;
};
